import React, { useEffect, useState, useCallback, useRef } from 'react';
import Plotly from 'plotly.js/dist/plotly';
import { useDashboard } from '../../context/DashboardContext';
import { cityCoords } from '../../data/impactsData';
import { 
  fetchGeoJSON, 
  fetchPowerOutageDataForWards, 
  fetchTemperatureDataForAllWards,
  fetchGroundwaterDataForWards,
  fetchInformalWorkersDataForWards,
  fetchConstructionLaborDataForWards,
  fetchInformalWorkersForecast,
  fetchConstructionLaborForecast,
  fetchTransportDisruptionsDataForWards,
  fetchRoadClosuresDataForWards,
  fetchTrafficSignalFailuresDataForWards,
  fetchCoolingSystemFailuresDataForWards,
  fetchSchoolClosuresDataForWards,
  fetchSchoolCoolingDataForWards,
  fetchWaterSupplyDataForWards,
  fetchWaterTankersDataForWards,
  fetchHeatDifferentialDataForWards,
  fetchSurfaceTemperatureDataForWards,
  fetchShelterOccupancyDataForWards
} from '../../services/api';
import { 
  getWardPropertyName,
  calculateCityBounds
} from '../../utils/mapUtils';
import { 
  calculateGroundwaterSeverityLevel, 
  calculateSeverityLevel, 
  calculateInformalWorkersSeverityLevel,
  calculateLostLaborHoursSeverityLevel,
  calculateNoShadeWaterSeverityLevel,
  calculateHeatIllnessSeverityLevel
} from '../../utils/severityUtils';
import DateRangeSelector from './DateRangeSelector';
import ForecastDateSelector from './ForecastDateSelector';

// Hook for responsive window size
const useWindowSize = () => {
  const [windowSize, setWindowSize] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
  });

  useEffect(() => {
    const handleResize = () => {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return windowSize;
};

const transportDisruptionsIndicatorName = 'Public transport disruptions (rail, metro, bus)';
const INDICATOR_NAMES = {
  roadClosures: 'Road/bridge degradation or closures',
  trafficSignal: 'Traffic signal / equipment failures',
  coolingSystem: 'Cooling system failure in public buildings',
  schoolClosures: 'No. of school/college closure days due to heat',
  schoolCooling: '% of schools without basic cooling infrastructure',
  waterSupply: 'Drop in per capita water availability (lpcd)',
  waterTankers: 'Percentage of wards dependent on water tankers',
  heatDifferential: 'Temperature differential (urban vs. rural)',
  surfaceTemperature: 'Surface temperature & low vegetation zones',
  shelterOccupancy: 'Homeless and street dwellers exposed'
};

const MapContainer = ({ 
  indicatorName, 
  city, 
  isPowerOutage, 
  powerOutageMode,
  isGroundwater,
  groundwaterMode,
  isInformalWorkers,
  informalWorkersMode,
  isConstructionLabor,
  constructionLaborMode,
  isTransportDisruptions,
  transportDisruptionsMode,
  isRoadClosures,
  roadClosuresMode,
  isTrafficSignalFailures,
  trafficSignalFailuresMode,
  isCoolingSystemFailures,
  coolingSystemFailuresMode,
  isSchoolClosures,
  schoolClosuresMode,
  isSchoolCooling,
  schoolCoolingMode,
  isWaterSupply,
  waterSupplyMode,
  isWaterTankers,
  waterTankersMode,
  isHeatDifferential,
  heatDifferentialMode,
  isSurfaceTemperature,
  surfaceTemperatureMode,
  isShelterOccupancy,
  shelterOccupancyMode,
  forecastDay,
  onForecastDayChange,
  onForecastDaysAvailable,
  selectedDate,
  showDateSelector,
  startDate,
  endDate,
  onStartDateChange,
  onEndDateChange,
  availableMonths
}) => {
  // Fixed zoom level for consistent map view
  const FIXED_ZOOM_LEVEL = 10.5;
  
  const mapDivRef = useRef(null);
  const windowSize = useWindowSize();
  const {
    currentGeoJSON,
    setCurrentGeoJSON,
    cityMapCenter,
    setCityMapCenter,
    setCityMapZoom,
    setWardDataMap,
    hasIndicatorData,
    selectedWard,
    setSelectedWard,
    wardDataMap,
    getIndicatorThreshold
  } = useDashboard();

  const [plotData, setPlotData] = useState([]);
  const [plotLayout, setPlotLayout] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [indiaGeoJSON, setIndiaGeoJSON] = useState(null);
  const [forecastDaysAvailable, setForecastDaysAvailable] = useState(6);
  const [severityCounts, setSeverityCounts] = useState({ LOW: 0, MEDIUM: 0, HIGH: 0, NO_DATA: 0 });

  const getGeometryBboxCenter = useCallback((geometry) => {
    if (!geometry || !geometry.coordinates) return null;
    let minLon = Infinity, maxLon = -Infinity, minLat = Infinity, maxLat = -Infinity;
    const visit = (node) => {
      if (!node) return;
      // [lon, lat]
      if (Array.isArray(node) && node.length === 2 && typeof node[0] === 'number' && typeof node[1] === 'number') {
        const lon = node[0];
        const lat = node[1];
        if (lon < minLon) minLon = lon;
        if (lon > maxLon) maxLon = lon;
        if (lat < minLat) minLat = lat;
        if (lat > maxLat) maxLat = lat;
        return;
      }
      if (Array.isArray(node)) {
        node.forEach(visit);
      }
    };
    visit(geometry.coordinates);
    if (!isFinite(minLon) || !isFinite(minLat) || !isFinite(maxLon) || !isFinite(maxLat)) return null;
    return { lon: (minLon + maxLon) / 2, lat: (minLat + maxLat) / 2 };
  }, []);

  // Note: no blinking animation — it caused full map re-renders/flash in Plotly Mapbox.
  // We use a steady “pop” style: thick gold outline + light gold fill for the selected ward.
  const lastSelectionAppliedRef = useRef({ selectedId: null, centerKey: null });

  // Apply selection by updating plotData/plotLayout (avoid Plotly.relayout/restyle).
  // IMPORTANT: only update state when values actually change, otherwise Plotly will re-render in a loop.
  useEffect(() => {
    if (isLoading) return;
    if (!currentGeoJSON) return;

    const wardPropertyName = getWardPropertyName(currentGeoJSON);
    const featureIdKey = `properties.${wardPropertyName}`;
    const selectedId = selectedWard?.id ?? selectedWard?.properties?.[wardPropertyName] ?? null;

    const selectedCenter = selectedWard?.geometry ? getGeometryBboxCenter(selectedWard.geometry) : null;
    const baseCenter = cityMapCenter || calculateCityBounds(currentGeoJSON).center;
    const effectiveCenter = selectedCenter
      ? { lat: selectedCenter.lat, lon: selectedCenter.lon }
      : baseCenter;
    const centerKey = effectiveCenter ? `${effectiveCenter.lat.toFixed(6)},${effectiveCenter.lon.toFixed(6)}` : null;

    // Update layout center only if it changed
    if (centerKey && lastSelectionAppliedRef.current.centerKey !== centerKey) {
      lastSelectionAppliedRef.current.centerKey = centerKey;
      setPlotLayout((prev) => {
        if (!prev?.mapbox) return prev;
        const prevCenter = prev.mapbox.center;
        if (prevCenter && prevCenter.lat === effectiveCenter.lat && prevCenter.lon === effectiveCenter.lon) return prev;
        return {
          ...prev,
          mapbox: {
            ...(prev.mapbox || {}),
            center: effectiveCenter
          }
        };
      });
    }

    // Update highlight trace only if selection changed
    if (lastSelectionAppliedRef.current.selectedId !== selectedId) {
      lastSelectionAppliedRef.current.selectedId = selectedId;
      setPlotData((prev) => {
        const arr = Array.isArray(prev) ? prev : [];
        const withoutHighlight = arr.filter((t) => t?.name !== 'selected-ward-highlight');
        if (selectedId == null) return withoutHighlight;
        const highlightTrace = {
          name: 'selected-ward-highlight',
          type: 'choroplethmapbox',
          geojson: currentGeoJSON,
          featureidkey: featureIdKey,
          locations: [selectedId],
          z: [1],
          colorscale: [[0, 'rgba(255, 215, 0, 0.25)'], [1, 'rgba(255, 215, 0, 0.25)']],
          showscale: false,
          hoverinfo: 'skip',
          marker: { opacity: 0.35, line: { width: 5, color: '#FFD700' } }
        };
        return withoutHighlight.concat(highlightTrace);
      });
    }
  }, [selectedWard, isLoading, currentGeoJSON, cityMapCenter, getGeometryBboxCenter]);
  
  const indicatorNameForThreshold = 'Power outages (duration and geographic scope)';
  const groundwaterIndicatorName = 'Groundwater stress levels';

  // Load India boundary GeoJSON
  useEffect(() => {
    const loadIndiaBoundary = async () => {
      try {
        const response = await fetch(`${process.env.PUBLIC_URL}/india/india-osm_2.json`);
        if (response.ok) {
          const geojson = await response.json();
          console.log('India boundary GeoJSON loaded:', geojson);
          console.log('Features count:', geojson.features?.length);
          if (geojson.features && geojson.features.length > 0) {
            console.log('First feature properties:', geojson.features[0].properties);
          }
          setIndiaGeoJSON(geojson);
          console.log('India boundary GeoJSON loaded successfully');
        } else {
          console.warn('Could not load India boundary GeoJSON:', response.status, response.statusText);
        }
      } catch (error) {
        console.warn('Error loading India boundary:', error);
      }
    };
    loadIndiaBoundary();
  }, []);

  // Helper function to extract coordinates from GeoJSON for boundary lines
  const extractBoundaryCoordinates = (geojson) => {
    const allLons = [];
    const allLats = [];
    
    const processCoordinates = (coords) => {
      if (Array.isArray(coords[0]) && Array.isArray(coords[0][0])) {
        // MultiPolygon or nested arrays
        coords.forEach(ring => processCoordinates(ring));
      } else if (Array.isArray(coords[0]) && typeof coords[0][0] === 'number') {
        // Polygon ring
        coords.forEach(coord => {
          const [lon, lat] = coord;
          allLons.push(lon);
          allLats.push(lat);
        });
        // Add null to break lines between rings
        allLons.push(null);
        allLats.push(null);
      }
    };
    
    geojson.features.forEach(feature => {
      if (feature.geometry) {
        if (feature.geometry.type === 'Polygon') {
          feature.geometry.coordinates.forEach(ring => processCoordinates(ring));
        } else if (feature.geometry.type === 'MultiPolygon') {
          feature.geometry.coordinates.forEach(polygon => {
            polygon.forEach(ring => processCoordinates(ring));
          });
        }
      }
    });
    
    return { lons: allLons, lats: allLats };
  };

  // Helper function to add India boundary trace using scattermapbox
  const addIndiaBoundaryTrace = (traces) => {
    if (indiaGeoJSON && indiaGeoJSON.features && indiaGeoJSON.features.length > 0) {
      // Extract coordinates from GeoJSON
      const { lons, lats } = extractBoundaryCoordinates(indiaGeoJSON);
      
      if (lons.length > 0 && lats.length > 0) {
        // Add India boundary as a scattermapbox trace with lines
        const boundaryTrace = {
          type: 'scattermapbox',
          mode: 'lines',
          lon: lons,
          lat: lats,
          line: {
            width: 3,
            color: '#0000FF' // Blue color like in the screenshot
          },
          hoverinfo: 'skip',
          showlegend: false,
          below: 'traces' // Render below other traces
        };
        
        traces.unshift(boundaryTrace);
        console.log('India boundary trace added with scattermapbox:', lons.length, 'points');
      } else {
        console.warn('Could not extract coordinates from India GeoJSON');
      }
    }
    return traces;
  };

  // Render simple city map (just ward boundaries, no data)
  const renderCityMap = useCallback((geojson, mapCenter, mapZoom) => {
    console.log('=== renderCityMap called ===');
    console.log('GeoJSON features count:', geojson?.features?.length);
    
    const wardPropertyName = getWardPropertyName(geojson);
    const featureIdKey = `properties.${wardPropertyName}`;
    
    const wardNumbers = [];
    const hoverTexts = [];
    const values = [];
    
    geojson.features.forEach((feature) => {
      const wardNum = feature.properties[wardPropertyName];
      const wardName = feature.properties.ward_name || feature.properties.Ward_Name || `Ward ${wardNum}`;
      
      wardNumbers.push(wardNum);
      values.push(1);
      hoverTexts.push(`${wardName}<br>Ward Number: ${wardNum}`);
    });
    
    console.log(`City map: ${wardNumbers.length} wards total`);
    
    const trace = {
      type: 'choroplethmapbox',
      geojson: geojson,
      locations: wardNumbers,
      z: values,
      featureidkey: featureIdKey,
      text: hoverTexts,
      hoverinfo: 'text',
      colorscale: [[0, '#3498db'], [1, '#2980b9']],
      zmin: 0,
      zmax: 1,
      marker: { opacity: 0.7, line: { width: 2, color: '#000000' } },
      showscale: false
    };

    const traces = [trace];
    addIndiaBoundaryTrace(traces);
    setPlotData(traces);
    setPlotLayout({
      mapbox: {
        style: 'open-street-map',
        center: mapCenter,
        zoom: FIXED_ZOOM_LEVEL,
        bearing: 0,
        pitch: 0
      },
      margin: { 
        t: 0, 
        b: 0, 
        l: 0, 
        r: window.innerWidth <= 480 ? 20 : (window.innerWidth <= 768 ? 40 : 50) 
      },
      height: window.innerWidth <= 480 ? 300 : (window.innerWidth <= 768 ? 400 : 500),
      autosize: true,
      dragmode: 'zoom',
      clickmode: 'event',
      uirevision: 'constant',
      hovermode: 'closest',
      hoverdistance: 5,
      hoverlabel: { bgcolor: 'white', bordercolor: '#333', font: { size: 12 }, namelength: -1 }
    });
    
    console.log('✅ City map rendering completed successfully');
  }, [indiaGeoJSON]);

  // Render forecast map
  const renderForecastMap = useCallback(async (geojson, mapCenter, mapZoom, forecastDate) => {
    console.log('=== renderForecastMap called ===');
    console.log('Forecast date received:', forecastDate);
    console.log('Forecast date type:', typeof forecastDate, forecastDate instanceof Date);
    
    // Format date for API (YYYY-MM-DD)
    const formatDateForAPI = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };
    
    // Always fetch from today to get full 7-day forecast
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const startDate = formatDateForAPI(today);
    
    // Calculate day offset for the selected forecast day
    const selectedDate = new Date(forecastDate);
    selectedDate.setHours(0, 0, 0, 0);
    const dayOffset = Math.floor((selectedDate - today) / (1000 * 60 * 60 * 24));
    
    console.log(`🗓️ Today: ${today.toDateString()}, Selected: ${selectedDate.toDateString()}, Day Offset: ${dayOffset}`);
    
    console.log(`Fetching forecast data for ${startDate}, selected day offset: ${dayOffset}`);
    
    const threshold = getIndicatorThreshold('Power outages');
    console.log(`Using threshold: ${threshold} for Power outages`);
    
    let forecastData;
    let rawForecastData; // Declare in outer scope for error handling
    let effectiveDayOffset = dayOffset; // Initialize with original offset
    try {
      // Import fetchPowerOutageForecast
      const { fetchPowerOutageForecast } = await import('../../services/api');
      rawForecastData = await fetchPowerOutageForecast(startDate, city);
      console.log('Raw forecast data received:', rawForecastData);
      
      // Notify parent about available days
      if (rawForecastData.days) {
        const availableDaysCount = rawForecastData.days;
        console.log('Available forecast days:', availableDaysCount);
        setForecastDaysAvailable(availableDaysCount);
        if (onForecastDaysAvailable) {
          onForecastDaysAvailable(availableDaysCount);
        }
      }
      
      // Validate day offset is within available forecast range
      if (dayOffset < 0 || dayOffset >= (rawForecastData.days || 7)) {
        throw new Error(`Day offset ${dayOffset} is outside forecast range (0-${(rawForecastData.days || 7) - 1}). Selected date may be outside the forecast window.`);
      }
      
      // Transform forecast data to map format
      // API returns ward_predictions array with predictions for each day starting from today
      // Use dayOffset to get the prediction for the selected day
      forecastData = {};
      if (rawForecastData.ward_predictions && Array.isArray(rawForecastData.ward_predictions)) {
        console.log('ward_predictions count:', rawForecastData.ward_predictions.length);
        
        let wardsWithData = 0;
        let wardsWithoutPredictionAtOffset = 0;
        let maxPredictionLength = 0;
        
        // First pass: check the actual prediction array lengths
        rawForecastData.ward_predictions.forEach((wardData) => {
          if (wardData.predictions && wardData.predictions.length > maxPredictionLength) {
            maxPredictionLength = wardData.predictions.length;
          }
        });
        
        console.log(`Max prediction array length found: ${maxPredictionLength}, requested day offset: ${dayOffset}`);
        
        // Only apply fallback if dayOffset actually exceeds available predictions
        if (dayOffset >= maxPredictionLength) {
          effectiveDayOffset = Math.max(0, maxPredictionLength - 1);
          console.warn(`Day offset ${dayOffset} exceeds available predictions (${maxPredictionLength}). Using fallback offset ${effectiveDayOffset} instead.`);
        } else {
          effectiveDayOffset = dayOffset;
          console.log(`Using requested day offset: ${effectiveDayOffset}`);
        }
        
        rawForecastData.ward_predictions.forEach((wardData, index) => {
          const wardNum = wardData.ward_number;
          // Get prediction for the selected day using effective day offset
          const prediction = wardData.predictions && wardData.predictions[effectiveDayOffset];
          
          if (index < 5) {
            console.log(`📊 Ward ${wardNum}:`);
            console.log(`  - Predictions array length: ${wardData.predictions?.length}`);
            console.log(`  - Accessing index: ${effectiveDayOffset}`);
            if (wardData.predictions && wardData.predictions.length > effectiveDayOffset) {
              console.log(`  - Date at offset ${effectiveDayOffset}: ${prediction?.date}`);
              console.log(`  - Severity Index: ${prediction?.predicted_severity_index}`);
              console.log(`  - Consumers affected: ${prediction?.predicted_consumers_affected}`);
              console.log(`  - Duration: ${prediction?.predicted_duration_minutes} mins`);
              console.log(`  - Temperature: ${prediction?.forecast_temp_c}°C`);
            } else {
              console.log(`  - ⚠️ No prediction at offset ${effectiveDayOffset}`);
            }
          }
          
          // Only treat as having data if predicted_severity_index is not null
          if (prediction && prediction.predicted_severity_index != null) {
            wardsWithData++;
            forecastData[wardNum] = {
              severity_index: prediction.predicted_severity_index,
              consumers_affected: prediction.predicted_consumers_affected || 0,
              duration_minutes: prediction.predicted_duration_minutes || 0,
              temperature: prediction.forecast_temp_c || 0,
              confidence: prediction.confidence || 'LOW',
              predicted_outages: prediction.predicted_outages || 0,
              date: prediction.date
            };
          } else {
            wardsWithoutPredictionAtOffset++;
          }
        });
        console.log(`Processed forecast data: ${wardsWithData} wards with data, ${wardsWithoutPredictionAtOffset} without prediction at offset ${effectiveDayOffset}`);
      } else {
        console.error('ward_predictions not found or not an array:', rawForecastData.ward_predictions);
      }
      if (Object.keys(forecastData).length > 0) {
        const firstWardData = forecastData[1] || forecastData[Object.keys(forecastData)[0]];
        console.log('First forecast sample:', firstWardData);
        console.log(`📈 Summary: Using day offset ${effectiveDayOffset} for date ${new Date(forecastDate).toDateString()}`);
        console.log(`📊 Ward 1 severity_index: ${firstWardData.severity_index}, temp: ${firstWardData.temperature}°C`);
      }
    } catch (apiError) {
      console.error('API error fetching forecast data:', apiError);
      throw new Error(`Failed to fetch forecast data from API: ${apiError.message}. Please check if the API server is running.`);
    }
    
    if (!forecastData || Object.keys(forecastData).length === 0) {
      let errorMsg;
      if (dayOffset !== effectiveDayOffset) {
        errorMsg = `Forecast data for ${new Date(selectedDate).toLocaleDateString()} (day ${dayOffset + 1}) is not available. Showing data for the last available forecast day instead.`;
      } else {
        errorMsg = `No forecast data available for date offset ${dayOffset} (${new Date(selectedDate).toLocaleDateString()}). `;
        const details = rawForecastData ? `API returned ${rawForecastData.ward_predictions?.length || 0} wards but none have data at day ${dayOffset}. Check if all predictions arrays include data for this day.` : 'API response structure unexpected.';
        errorMsg += details;
      }
      throw new Error(errorMsg);
    }

    const wardPropertyName = getWardPropertyName(geojson);
    const featureIdKey = `properties.${wardPropertyName}`;

    const dataWardNumbers = [];
    const dataValues = [];
    const dataHoverTexts = [];
    const noDataWardNumbers = [];
    const noDataHoverTexts = [];
    const newWardDataMap = {};
    const counts = { LOW: 0, MEDIUM: 0, HIGH: 0, NO_DATA: 0 };

    geojson.features.forEach((feature) => {
      const wardNum = feature.properties[wardPropertyName];
      const wardName = feature.properties.ward_name || feature.properties.Ward_Name || `Ward ${wardNum}`;
      
      let numericWardNum = null;
      if (typeof wardNum === 'number') {
        numericWardNum = wardNum;
      } else if (typeof wardNum === 'string') {
        const parsed = parseInt(wardNum, 10);
        if (!isNaN(parsed)) {
          numericWardNum = parsed;
        }
      }
      
      const data = numericWardNum !== null ? forecastData[numericWardNum] : null;

      newWardDataMap[wardNum] = {
        id: wardNum,
        name: wardName,
        properties: feature.properties,
        geometry: feature.geometry,
        data: data,
        numericWardNum: numericWardNum
      };

      // Only treat as having data if severity_index is not null (same logic as table)
      if (data && data.severity_index != null) {
        dataWardNumbers.push(wardNum);

        // Use the predicted_severity_index directly from the API forecast
        // The API provides day-specific severity predictions that account for
        // various factors including temperature, historical patterns, etc.
        const severityIndex = data.severity_index;
        
        // Calculate severity level using threshold-based calculation
        const severityLevel = calculateSeverityLevel(severityIndex, threshold);
        
        // Count wards by severity level
        if (severityLevel === 'LOW') counts.LOW++;
        else if (severityLevel === 'MEDIUM') counts.MEDIUM++;
        else if (severityLevel === 'HIGH') counts.HIGH++;
        
        const severityMap = { 'LOW': 1, 'MEDIUM': 2, 'HIGH': 3 };
        const severityValue = severityMap[severityLevel] || 1;
        
        // Severity color mapping
        const severityColors = {
          'HIGH': '#B71C1C',
          'MEDIUM': '#F9A825',
          'LOW': '#2E7D32',
          'N/A': '#808080'
        };
        const severityColor = severityColors[severityLevel] || '#808080';
        
        dataValues.push(severityValue);
        const durationStr = (data.duration_minutes || 0).toFixed(0);
        const consumersStr = (data.consumers_affected || 0).toLocaleString();
        
        // Format hover text with severity color
        dataHoverTexts.push(
          `Ward Name: ${wardName} (${wardNum})<br>` +
          `<span style="color: ${severityColor}; font-weight: bold;">Severity: ${severityLevel}</span><br>` +
          `Duration of Unplanned Outage: ${durationStr} mins<br>` +
          `Total Consumers Affected: ${consumersStr}`
        );
      } else {
        noDataWardNumbers.push(wardNum);
        counts.NO_DATA++;
        noDataHoverTexts.push(`Ward Name: ${wardName} (${wardNum})<br>No Forecast data availbe for this ward`);
      }
    });

    setWardDataMap(newWardDataMap);
    setSeverityCounts(counts);

    // Use same color scale as severity map (green → orange → red)
    const colorscale = [[0, '#2E7D32'], [0.5, '#F9A825'], [1, '#B71C1C']];
    const colorbarTitle = 'Forecasted Severity';
    const zmin = 1;
    const zmax = 3;
    const tickvals = [1, 2, 3];
    const ticktext = ['LOW', 'MEDIUM', 'HIGH'];

    // Wards with data
    const dataTrace = {
      type: 'choroplethmapbox',
      geojson: geojson,
      featureidkey: featureIdKey,
      locations: dataWardNumbers,
      z: dataValues,
      colorscale: colorscale,

      zmin: zmin,
      zmax: zmax,
      showscale: false,
      hoverinfo: 'text',
      hovertext: dataHoverTexts,
      marker: { 
        opacity: 0.7,
        line: { 
          color: '#000000',
          width: 1.5
        }
      },
      hoverlabel: { bgcolor: 'white', bordercolor: '#333', font: { size: 12 }, namelength: -1 }
    };

    // Wards with no data - same grey as severity "No Unplanned outage"
    const noDataTrace = {
      type: 'choroplethmapbox',
      geojson: geojson,
      featureidkey: featureIdKey,
      locations: noDataWardNumbers,
      z: Array(noDataWardNumbers.length).fill(0),
      colorscale: [[0, '#808080'], [1, '#404040']],
      zmin: 0,
      zmax: 1,
      showscale: false,
      hoverinfo: 'text',
      hovertext: noDataHoverTexts,
      marker: { 
        opacity: 0.7,
        line: { color: '#000000', width: 1.5 }
      },
      hoverlabel: { bgcolor: 'white', bordercolor: '#333', font: { size: 12 }, namelength: -1 }
    };

    // Highlight selected ward
    let selectedWardTrace = null;
    if (selectedWard) {
      const selectedWardNum = selectedWard.id || selectedWard.properties?.[wardPropertyName];
      if (selectedWardNum) {
        selectedWardTrace = {
          name: 'selected-ward-highlight',
          type: 'choroplethmapbox',
          geojson: geojson,
          featureidkey: featureIdKey,
          locations: [selectedWardNum],
          z: [1],
          colorscale: [[0, 'rgba(255, 215, 0, 0.25)'], [1, 'rgba(255, 215, 0, 0.25)']],
          showscale: false,
          hoverinfo: 'skip',
          marker: { 
            opacity: 0.35,
            line: { color: '#FFD700', width: 5 }
          }
        };
      }
    }

    const traces = selectedWardTrace 
      ? [noDataTrace, dataTrace, selectedWardTrace]
      : [noDataTrace, dataTrace];

    // Add India boundary layer using helper function
    addIndiaBoundaryTrace(traces);

    const selectedCenter = selectedWard?.geometry ? getGeometryBboxCenter(selectedWard.geometry) : null;
    const effectiveCenter = selectedCenter ? { lat: selectedCenter.lat, lon: selectedCenter.lon } : { lat: mapCenter.lat, lon: mapCenter.lon };
    const layout = {
      mapbox: {
        style: 'open-street-map',
        center: effectiveCenter,
        zoom: mapZoom
      },
      margin: { 
        t: 0, 
        b: 0, 
        l: 0, 
        r: window.innerWidth <= 480 ? 20 : (window.innerWidth <= 768 ? 40 : 50) 
      },
      showlegend: false,
      height: window.innerWidth <= 480 ? 300 : (window.innerWidth <= 768 ? 400 : 500),
      autosize: true,
      paper_bgcolor: 'rgba(0,0,0,0)',
      plot_bgcolor: 'rgba(0,0,0,0)',
      clickmode: 'event',
      uirevision: `forecast-${forecastDate.getTime()}`,
      hovermode: 'closest',
      hoverdistance: 5,
      hoverlabel: { bgcolor: 'white', bordercolor: '#333', font: { size: 12 }, namelength: -1 }
    };

    setPlotData(traces);
    setPlotLayout(layout);
    setIsLoading(false);
    setError(null);
    
    console.log(`✅ Forecast map rendering completed successfully for day offset ${dayOffset} (${forecastDate.toDateString()})`);
  }, [indiaGeoJSON, selectedWard, setWardDataMap, getIndicatorThreshold, getGeometryBboxCenter]);

  // Render groundwater forecast map
  const renderGroundwaterForecastMap = useCallback(async (geojson, mapCenter, mapZoom, forecastDate) => {
    console.log('=== renderGroundwaterForecastMap called ===');
    console.log('Forecast date received:', forecastDate);
    
    // Use current system date as "today" for the 7-day forecast window (API fetches all wards, 7 days from today)
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    // Calculate day offset for the selected forecast day (0 = today, 1 = tomorrow, ...)
    const selectedDate = new Date(forecastDate);
    selectedDate.setHours(0, 0, 0, 0);
    const dayOffset = Math.floor((selectedDate - today) / (1000 * 60 * 60 * 24));
    
    console.log(`🗓️ Today: ${today.toDateString()}, Selected: ${selectedDate.toDateString()}, Day Offset: ${dayOffset}`);
    
    const threshold = getIndicatorThreshold(groundwaterIndicatorName);
    console.log(`Using threshold: ${threshold} for ${groundwaterIndicatorName}`);
    
    let forecastData;
    let rawForecastData;
    let effectiveDayOffset = dayOffset;
    try {
      // All wards, next 7 days from current system date (no ward_id; start_date = today)
      const { fetchGroundwaterForecast } = await import('../../services/api');
      rawForecastData = await fetchGroundwaterForecast({ city });
      console.log('Raw groundwater forecast data received:', rawForecastData);
      console.log('API Response structure:', {
        hasWardPredictions: !!rawForecastData.ward_predictions,
        wardPredictionsLength: rawForecastData.ward_predictions?.length,
        hasDailySummary: !!rawForecastData.daily_summary,
        dailySummaryLength: rawForecastData.daily_summary?.length,
        indicator: rawForecastData.indicator
      });
      
      // Notify parent about available days
      if (rawForecastData.daily_summary && rawForecastData.daily_summary.length > 0) {
        const availableDaysCount = rawForecastData.daily_summary.length;
        console.log('Available forecast days:', availableDaysCount);
        setForecastDaysAvailable(availableDaysCount);
        if (onForecastDaysAvailable) {
          onForecastDaysAvailable(availableDaysCount);
        }
      }
      
      // Validate day offset is within available forecast range
      const maxDays = rawForecastData.daily_summary?.length || 7;
      if (dayOffset < 0 || dayOffset >= maxDays) {
        throw new Error(`Day offset ${dayOffset} is outside forecast range (0-${maxDays - 1}). Selected date may be outside the forecast window.`);
      }
      
      // Transform forecast data to map format (ward_predictions is normalized by API to always be an array)
      forecastData = {};
      const wardPredictions = rawForecastData.ward_predictions && Array.isArray(rawForecastData.ward_predictions)
        ? rawForecastData.ward_predictions
        : [];
      
      if (wardPredictions.length === 0) {
        console.warn('API returned no ward predictions for groundwater forecast');
        // Fall through to empty state handling below
      } else {
        console.log('ward_predictions count:', wardPredictions.length);
        
        // Log sample ward data structure for debugging
        if (wardPredictions.length > 0) {
          const sampleWard = wardPredictions[0];
          console.log('Sample ward structure:', {
            ward_id: sampleWard.ward_id,
            ward_number: sampleWard.ward_number,
            ward_name: sampleWard.ward_name,
            predictionsLength: sampleWard.predictions?.length,
            firstPrediction: sampleWard.predictions?.[0]
          });
        }
        
        let wardsWithData = 0;
        let maxPredictionLength = 0;
        
        // First pass: check the actual prediction array lengths
        wardPredictions.forEach((wardData) => {
          if (wardData.predictions && wardData.predictions.length > maxPredictionLength) {
            maxPredictionLength = wardData.predictions.length;
          }
        });
        
        console.log(`Max prediction array length found: ${maxPredictionLength}, requested day offset: ${dayOffset}`);
        
        // Only apply fallback if dayOffset actually exceeds available predictions
        if (dayOffset >= maxPredictionLength) {
          effectiveDayOffset = Math.max(0, maxPredictionLength - 1);
          console.warn(`Day offset ${dayOffset} exceeds available predictions (${maxPredictionLength}). Using fallback offset ${effectiveDayOffset} instead.`);
        } else {
          effectiveDayOffset = dayOffset;
          console.log(`Using requested day offset: ${effectiveDayOffset}`);
        }
        
        wardPredictions.forEach((wardData, index) => {
          const wardNum = wardData.ward_number;
          // Get prediction for the selected day using effective day offset
          const prediction = wardData.predictions && wardData.predictions[effectiveDayOffset];
          
          // Debug logging for first few wards
          if (index < 5) {
            console.log(`📊 Ward ${wardNum}:`);
            console.log(`  - Predictions array length: ${wardData.predictions?.length}`);
            console.log(`  - Accessing index: ${effectiveDayOffset}`);
            if (wardData.predictions && wardData.predictions.length > effectiveDayOffset) {
              console.log(`  - Date at offset ${effectiveDayOffset}: ${prediction?.date}`);
              console.log(`  - Depletion Level: ${prediction?.predicted_depletion_level_meters}`);
              console.log(`  - Current GWL: ${prediction?.predicted_current_gwl_meters}`);
              console.log(`  - Mean GWL: ${prediction?.predicted_mean_gwl_meters}`);
            } else {
              console.log(`  - ⚠️ No prediction at offset ${effectiveDayOffset}`);
            }
          }
          
          if (prediction) {
            // Include every ward: with values or nulls (API returns all wards; some have null predictions)
            const hasData = prediction.predicted_depletion_level_meters != null ||
                           prediction.predicted_current_gwl_meters != null ||
                           prediction.predicted_mean_gwl_meters != null;
            if (hasData) wardsWithData++;

            const depletionLevel = prediction.predicted_depletion_level_meters != null
              ? Number(prediction.predicted_depletion_level_meters)
              : 0;
            const severityLevel = hasData
              ? calculateGroundwaterSeverityLevel(depletionLevel, threshold)
              : null; // null = no data, show as grey on map

            forecastData[wardNum] = {
              depletion_level_meters: hasData ? depletionLevel : null,
              current_gwl_meters: prediction.predicted_current_gwl_meters != null
                ? Number(prediction.predicted_current_gwl_meters)
                : null,
              mean_gwl_meters: prediction.predicted_mean_gwl_meters != null
                ? Number(prediction.predicted_mean_gwl_meters)
                : null,
              severity_level: severityLevel,
              date: prediction.date
            };
          }
        });
        console.log(`Processed forecast data: ${wardsWithData} wards with data`);
      }
    } catch (apiError) {
      console.error('API error fetching groundwater forecast data:', apiError);
      throw new Error(`Failed to fetch groundwater forecast data from API: ${apiError.message}. Please check if the API server is running.`);
    }
    
    const hasNoData = !forecastData || Object.keys(forecastData).length === 0;
    if (hasNoData) {
      const wardPredictionsList = rawForecastData?.ward_predictions || [];
      const wardCount = wardPredictionsList.length;
      console.warn('No groundwater forecast data:', {
        dayOffset,
        selectedDate: new Date(selectedDate).toLocaleDateString(),
        wardCount,
        wardsWithPredictions: wardCount ? wardPredictionsList.filter(w => w.predictions && w.predictions.length > 0).length : 0
      });
      // Continue to render base map with all wards as "no data" and show message
    }

    const wardPropertyName = getWardPropertyName(geojson);
    const featureIdKey = `properties.${wardPropertyName}`;

    const dataWardNumbers = [];
    const dataValues = [];
    const dataHoverTexts = [];
    const noDataWardNumbers = [];
    const noDataHoverTexts = [];
    const newWardDataMap = {};
    const counts = { LOW: 0, MEDIUM: 0, HIGH: 0, NO_DATA: 0 };

    geojson.features.forEach((feature) => {
      const wardNum = feature.properties[wardPropertyName];
      const wardName = feature.properties.ward_name || feature.properties.Ward_Name || `Ward ${wardNum}`;
      
      let numericWardNum = null;
      if (typeof wardNum === 'number') {
        numericWardNum = wardNum;
      } else if (typeof wardNum === 'string') {
        const parsed = parseInt(wardNum, 10);
        if (!isNaN(parsed)) {
          numericWardNum = parsed;
        }
      }
      
      const data = numericWardNum !== null ? forecastData[numericWardNum] : null;

      newWardDataMap[wardNum] = {
        id: wardNum,
        name: wardName,
        properties: feature.properties,
        geometry: feature.geometry,
        data: data,
        numericWardNum: numericWardNum
      };

      if (data) {
        const hasValues = data.severity_level != null;
        if (hasValues) {
          dataWardNumbers.push(wardNum);
          const meanGwl = data.mean_gwl_meters != null && !isNaN(data.mean_gwl_meters) ? data.mean_gwl_meters : null;
          const severityLevel = data.severity_level || 'LOW';
          
          // Count wards by severity level
          if (severityLevel === 'LOW') counts.LOW++;
          else if (severityLevel === 'MEDIUM') counts.MEDIUM++;
          else if (severityLevel === 'HIGH') counts.HIGH++;
          
          const severityMap = { 'LOW': 1, 'MEDIUM': 2, 'HIGH': 3 };
          const severityValue = severityMap[severityLevel] || 1;
          dataValues.push(severityValue);
          
          // Severity color mapping
          const severityColors = {
            'HIGH': '#B71C1C',
            'MEDIUM': '#F9A825',
            'LOW': '#2E7D32',
            'N/A': '#808080'
          };
          const severityColor = severityColors[severityLevel] || '#808080';
          
          const depStr = data.depletion_level_meters != null ? data.depletion_level_meters.toFixed(2) : 'N/A';
          const curStr = data.current_gwl_meters != null ? data.current_gwl_meters.toFixed(2) : 'N/A';
          const meanStr = meanGwl != null ? meanGwl.toFixed(2) : 'N/A';
          
          // Format hover text with severity color
          dataHoverTexts.push(
            `Ward Name: ${wardName} (${wardNum})<br>` +
            `<span style="color: ${severityColor}; font-weight: bold;">Severity: ${severityLevel}</span><br>` +
            `Predicted Depletion: ${depStr} m<br>` +
            `Predicted Current GWL: ${curStr} m<br>` +
            `Predicted Mean GWL: ${meanStr} m`
          );
        } else {
          noDataWardNumbers.push(wardNum);
          counts.NO_DATA++;
          noDataHoverTexts.push(`Ward Name: ${wardName} (${wardNum})<br>No Forecast data availbe for this ward`);
        }
      } else {
        noDataWardNumbers.push(wardNum);
        counts.NO_DATA++;
        noDataHoverTexts.push(`Ward Name: ${wardName} (${wardNum})<br>No Forecast data availbe for this ward`);
      }
    });

    setWardDataMap(newWardDataMap);
    setSeverityCounts(counts);

    // Use same color scale as severity map (green → orange → red)
    const colorscale = [[0, '#2E7D32'], [0.5, '#F9A825'], [1, '#B71C1C']];
    const colorbarTitle = 'Forecasted Severity';
    const zmin = 1;
    const zmax = 3;
    const tickvals = [1, 2, 3];
    const ticktext = ['LOW', 'MEDIUM', 'HIGH'];

    // Wards with data (only add trace if we have data)
    const dataTrace = dataWardNumbers.length > 0 ? {
      type: 'choroplethmapbox',
      geojson: geojson,
      featureidkey: featureIdKey,
      locations: dataWardNumbers,
      z: dataValues,
      colorscale: colorscale,
      zmin: zmin,
      zmax: zmax,
      showscale: false,
      hoverinfo: 'text',
      hovertext: dataHoverTexts,
      marker: { 
        opacity: 0.7,
        line: { 
          color: '#000000',
          width: 1.5
        }
      },
      hoverlabel: { bgcolor: 'white', bordercolor: '#333', font: { size: 12 }, namelength: -1 }
    } : null;

    // Wards with no data (always show so map has base layer)
    const noDataTrace = {
      type: 'choroplethmapbox',
      geojson: geojson,
      featureidkey: featureIdKey,
      locations: noDataWardNumbers,
      z: Array(noDataWardNumbers.length).fill(0),
      colorscale: [[0, '#808080'], [1, '#404040']],
      zmin: 0,
      zmax: 1,
      showscale: false,
      hoverinfo: 'text',
      hovertext: noDataHoverTexts,
      marker: { 
        opacity: 0.7,
        line: { color: '#000000', width: 1.5 }
      },
      hoverlabel: { bgcolor: 'white', bordercolor: '#333', font: { size: 12 }, namelength: -1 }
    };

    // Highlight selected ward
    let selectedWardTrace = null;
    if (selectedWard) {
      const selectedWardNum = selectedWard.id || selectedWard.properties?.[wardPropertyName];
      if (selectedWardNum) {
        selectedWardTrace = {
          name: 'selected-ward-highlight',
          type: 'choroplethmapbox',
          geojson: geojson,
          featureidkey: featureIdKey,
          locations: [selectedWardNum],
          z: [1],
          colorscale: [[0, 'rgba(255, 215, 0, 0.25)'], [1, 'rgba(255, 215, 0, 0.25)']],
          showscale: false,
          hoverinfo: 'skip',
          marker: { 
            opacity: 0.35,
            line: { color: '#FFD700', width: 5 }
          }
        };
      }
    }

    const traces = [noDataTrace]
      .concat(dataTrace ? [dataTrace] : [])
      .concat(selectedWardTrace ? [selectedWardTrace] : []);

    // Add India boundary layer using helper function
    addIndiaBoundaryTrace(traces);

    const selectedCenter = selectedWard?.geometry ? getGeometryBboxCenter(selectedWard.geometry) : null;
    const effectiveCenter = selectedCenter ? { lat: selectedCenter.lat, lon: selectedCenter.lon } : { lat: mapCenter.lat, lon: mapCenter.lon };
    const layout = {
      mapbox: {
        style: 'open-street-map',
        center: effectiveCenter,
        zoom: mapZoom
      },
      margin: { 
        t: 0, 
        b: 0, 
        l: 0, 
        r: window.innerWidth <= 480 ? 20 : (window.innerWidth <= 768 ? 40 : 50) 
      },
      showlegend: false,
      height: window.innerWidth <= 480 ? 300 : (window.innerWidth <= 768 ? 400 : 500),
      autosize: true,
      paper_bgcolor: 'rgba(0,0,0,0)',
      plot_bgcolor: 'rgba(0,0,0,0)',
      clickmode: 'event',
      uirevision: `groundwater-forecast-${forecastDate.getTime()}`,
      hovermode: 'closest',
      hoverdistance: 5,
      hoverlabel: { bgcolor: 'white', bordercolor: '#333', font: { size: 12 }, namelength: -1 }
    };

    setPlotData(traces);
    setPlotLayout(layout);
    setIsLoading(false);
    setError(hasNoData ? 'No forecast data available for this date. The API did not return ward-level predictions—check that the groundwater forecast endpoint has data for the selected date.' : null);
    
    console.log(`✅ Groundwater forecast map rendering completed for day offset ${effectiveDayOffset} (${forecastDate.toDateString()})${hasNoData ? ' (no data)' : ''}`);
  }, [indiaGeoJSON, selectedWard, setWardDataMap, getIndicatorThreshold, groundwaterIndicatorName, onForecastDaysAvailable, setForecastDaysAvailable, getGeometryBboxCenter]);

  // Render power outage map
  const renderPowerOutageMap = useCallback(async (geojson, mapCenter, mapZoom, mode) => {
    console.log('=== renderPowerOutageMap called ===');
    
    // Calculate date range from selectedDate
    let startDate = '2025-05-01';
    let endDate = '2025-05-31';
    
    if (selectedDate) {
      const year = selectedDate.getFullYear();
      const month = selectedDate.getMonth() + 1;
      startDate = `${year}-${String(month).padStart(2, '0')}-01`;
      const lastDay = new Date(year, month, 0).getDate();
      endDate = `${year}-${String(month).padStart(2, '0')}-${lastDay}`;
    }
    
    console.log(`Fetching power outage data from ${startDate} to ${endDate}`);
    // Get threshold for power outage indicator - always get fresh value
    const indicatorName = 'Power outages (duration and geographic scope)';
    const threshold = getIndicatorThreshold(indicatorName);
    console.log(`Using threshold: ${threshold} for ${indicatorName}`);
    let powerData;
    try {
      powerData = await fetchPowerOutageDataForWards(startDate, endDate, threshold, city);
      console.log('Power data received:', Object.keys(powerData).length, 'wards');
    } catch (apiError) {
      console.error('API error fetching power outage data:', apiError);
      throw new Error(`Failed to fetch power outage data from API: ${apiError.message}. Please check if the API server is running at https://heatassessmentapi.geedsim.com`);
    }
    
    if (!powerData || Object.keys(powerData).length === 0) {
      throw new Error(`Power outage data is not available for this period (${startDate} to ${endDate}). The API returned no data.`);
    }

    const wardPropertyName = getWardPropertyName(geojson);
    const featureIdKey = `properties.${wardPropertyName}`;

    const dataWardNumbers = [];
    const dataValues = [];
    const dataHoverTexts = [];
    const noDataWardNumbers = [];
    const noDataHoverTexts = [];
    const newWardDataMap = {};

    geojson.features.forEach((feature) => {
      const wardNum = feature.properties[wardPropertyName];
      const wardName = feature.properties.ward_name || feature.properties.Ward_Name || `Ward ${wardNum}`;
      
      let numericWardNum = null;
      if (typeof wardNum === 'number') {
        numericWardNum = wardNum;
      } else if (typeof wardNum === 'string') {
        const parsed = parseInt(wardNum, 10);
        if (!isNaN(parsed)) {
          numericWardNum = parsed;
        }
      }
      
      const data = numericWardNum !== null ? powerData[numericWardNum] : null;

      newWardDataMap[wardNum] = {
        id: wardNum,
        name: wardName,
        properties: feature.properties,
        geometry: feature.geometry,
        data: data,
        numericWardNum: numericWardNum
      };

      if (data) {
        dataWardNumbers.push(wardNum);

        const normalizedSeverity = data.dominant_severity ? String(data.dominant_severity).toUpperCase() : 'N/A';
        const displaySeverity = normalizedSeverity === 'CRITICAL' ? 'HIGH' : normalizedSeverity;
        
        if (mode === 'duration') {
          dataValues.push(data.total_duration_minutes);
          dataHoverTexts.push(
            `Ward Name: ${wardName}<br>Duration of Unplanned Outage: ${data.total_duration_minutes.toFixed(0)} mins<br>Total Consumers Affected: ${data.total_consumers_affected.toLocaleString()}<br>Severity Level: ${displaySeverity}`
          );
        } else if (mode === 'consumers') {
          dataValues.push(data.total_consumers_affected);
          dataHoverTexts.push(
            `Ward Name: ${wardName}<br>Duration of Unplanned Outage: ${data.total_duration_minutes.toFixed(0)} mins<br>Total Consumers Affected: ${data.total_consumers_affected.toLocaleString()}<br>Severity Level: ${displaySeverity}`
          );
        } else {
          // Fold CRITICAL into HIGH so the map shows three-level severity
          const severityMap = { 'LOW': 1, 'MEDIUM': 2, 'HIGH': 3, 'CRITICAL': 3 };
          dataValues.push(severityMap[displaySeverity] || 1);
          dataHoverTexts.push(
            `Ward Name: ${wardName}<br>Duration of Unplanned Outage: ${data.total_duration_minutes.toFixed(0)} mins<br>Total Consumers Affected: ${data.total_consumers_affected.toLocaleString()}<br>Severity Level: ${displaySeverity}`
          );
        }
      } else {
        noDataWardNumbers.push(wardNum);
        noDataHoverTexts.push(`Ward Name: ${wardName}<br>No Unplanned outage.`);
      }
    });

    setWardDataMap(newWardDataMap);

    let colorscale, colorbarTitle, zmin, zmax, tickvals, ticktext;
    
    if (mode === 'duration') {
      colorscale = [[0, '#ffffcc'], [0.25, '#ffeda0'], [0.5, '#feb24c'], [0.75, '#f03b20'], [1, '#bd0026']];
      colorbarTitle = 'Unplanned Power Outage Duration (minutes)';
      zmin = Math.min(...dataValues);
      zmax = Math.max(...dataValues);
    } else if (mode === 'consumers') {
      colorscale = [[0, '#f7fbff'], [0.25, '#c6dbef'], [0.5, '#6baed6'], [0.75, '#2171b5'], [1, '#08306b']];
      colorbarTitle = 'Consumers Affected';
      zmin = Math.min(...dataValues);
      zmax = Math.max(...dataValues);
    } else {
      // Updated colors for three-level severity (critical folded into high)
      colorscale = [[0, '#2E7D32'], [0.5, '#F9A825'], [1, '#B71C1C']];
      colorbarTitle = 'Severity Level';
      zmin = 1;
      zmax = 3;
      tickvals = [1, 2, 3];
      ticktext = ['LOW', 'MEDIUM', 'HIGH'];
    }

    const traces = [];

    if (dataWardNumbers.length > 0) {
      traces.push({
        type: 'choroplethmapbox',
        geojson: geojson,
        locations: dataWardNumbers,
        z: dataValues,
        featureidkey: featureIdKey,
        text: dataHoverTexts,
        hoverinfo: 'text',
        colorscale: colorscale,
        zmin: zmin,
        zmax: zmax,
        marker: { opacity: 0.7, line: { width: 1.5, color: '#000000' } },
        colorbar: mode === 'severity' ? undefined : {
          title: { text: colorbarTitle, side: 'right' },
          thickness: 15,
          len: 0.7,
          x: 1.02,
          tickvals: tickvals,
          ticktext: ticktext
        },
        showscale: mode !== 'severity'
      });
    }

    if (noDataWardNumbers.length > 0) {
      traces.push({
        type: 'choroplethmapbox',
        geojson: geojson,
        locations: noDataWardNumbers,
        z: new Array(noDataWardNumbers.length).fill(0),
        featureidkey: featureIdKey,
        text: noDataHoverTexts,
        hoverinfo: 'text',
        colorscale: [[0, '#808080'], [1, '#404040']],
        zmin: 0,
        zmax: 1,
        marker: { opacity: 0.7, line: { width: 1.5, color: '#000000' } },
        showscale: false
      });
    }

    // Add selected ward highlight
    if (selectedWard && selectedWard.id) {
      traces.push({
        name: 'selected-ward-highlight',
        type: 'choroplethmapbox',
        geojson: geojson,
        locations: [selectedWard.id],
        z: [1],
        featureidkey: featureIdKey,
        hoverinfo: 'skip',
        colorscale: [[0, 'rgba(255, 215, 0, 0.25)'], [1, 'rgba(255, 215, 0, 0.25)']],
        zmin: 0,
        zmax: 1,
        marker: { opacity: 0.35, line: { width: 5, color: '#FFD700' } },
        showscale: false
      });
    }

    addIndiaBoundaryTrace(traces);
    setPlotData(traces);
    setPlotLayout({
      mapbox: {
        style: 'open-street-map',
        center: mapCenter,
        zoom: FIXED_ZOOM_LEVEL,
        bearing: 0,
        pitch: 0
      },
      margin: { 
        t: 0, 
        b: 0, 
        l: 0, 
        // For severity mode, no colorbar so no right margin needed
        r: mode === 'severity' ? 0 : (window.innerWidth <= 480 ? 20 : (window.innerWidth <= 768 ? 40 : 50))
      },
      height: window.innerWidth <= 480 ? 300 : (window.innerWidth <= 768 ? 400 : 500),
      autosize: true,
      dragmode: 'zoom',
      clickmode: 'event',
      uirevision: 'constant',
      hovermode: 'closest',
      hoverdistance: 5,
      hoverlabel: { bgcolor: 'white', bordercolor: '#333', font: { size: 12 }, namelength: -1 }
    });
    
    console.log('✅ Power outage map rendering completed successfully');
  }, [setWardDataMap, selectedWard, selectedDate, getIndicatorThreshold, indiaGeoJSON]);

  // Render transport disruptions map (severity, commuters_affected, disruption_minutes)
  const renderTransportDisruptionsMap = useCallback(async (geojson, mapCenter, mapZoom, mode) => {
    let startDateStr = '2025-05-01';
    let endDateStr = '2025-05-31';
    if (startDate && endDate) {
      startDateStr = `${startDate.getFullYear()}-${String(startDate.getMonth() + 1).padStart(2, '0')}-${String(startDate.getDate()).padStart(2, '0')}`;
      endDateStr = `${endDate.getFullYear()}-${String(endDate.getMonth() + 1).padStart(2, '0')}-${String(endDate.getDate()).padStart(2, '0')}`;
    } else if (selectedDate) {
      const y = selectedDate.getFullYear();
      const m = selectedDate.getMonth() + 1;
      startDateStr = `${y}-${String(m).padStart(2, '0')}-01`;
      const lastDay = new Date(y, m, 0).getDate();
      endDateStr = `${y}-${String(m).padStart(2, '0')}-${lastDay}`;
    }
    const threshold = getIndicatorThreshold(transportDisruptionsIndicatorName);
    let transportData;
    try {
      transportData = await fetchTransportDisruptionsDataForWards(startDateStr, endDateStr, threshold, city);
    } catch (apiError) {
      console.error('API error fetching transport disruptions data:', apiError);
      throw new Error(`Failed to fetch transport disruptions data: ${apiError.message}`);
    }
    if (!transportData || typeof transportData !== 'object') {
      transportData = {};
    }

    const wardPropertyName = getWardPropertyName(geojson);
    const featureIdKey = `properties.${wardPropertyName}`;
    const dataWardNumbers = [];
    const dataValues = [];
    const dataHoverTexts = [];
    const noDataWardNumbers = [];
    const noDataHoverTexts = [];
    const newWardDataMap = {};
    const counts = { LOW: 0, MEDIUM: 0, HIGH: 0 };

    geojson.features.forEach((feature) => {
      const wardNum = feature.properties[wardPropertyName];
      const wardName = feature.properties.ward_name || feature.properties.Ward_Name || `Ward ${wardNum}`;
      let numericWardNum = null;
      if (typeof wardNum === 'number') numericWardNum = wardNum;
      else if (typeof wardNum === 'string') {
        const parsed = parseInt(wardNum, 10);
        if (!isNaN(parsed)) numericWardNum = parsed;
      }
      const data = numericWardNum != null ? transportData[numericWardNum] : null;

      newWardDataMap[wardNum] = {
        id: wardNum,
        name: wardName,
        properties: feature.properties,
        geometry: feature.geometry,
        data: data,
        numericWardNum: numericWardNum
      };

      if (data) {
        dataWardNumbers.push(wardNum);
        const displaySeverity = (data.dominant_severity || 'LOW').toUpperCase();
        if (mode === 'severity') {
          const severityMap = { LOW: 1, MEDIUM: 2, HIGH: 3 };
          dataValues.push(severityMap[displaySeverity] || 1);
          counts[displaySeverity] = (counts[displaySeverity] || 0) + 1;
          dataHoverTexts.push(
            `Ward Name: ${wardName}<br>Commuters Affected: ${(data.commuters_affected != null ? data.commuters_affected.toLocaleString() : 'N/A')}<br>Disruption Minutes: ${data.disruption_minutes != null ? data.disruption_minutes.toFixed(0) : 'N/A'} mins<br>Severity: ${displaySeverity}`
          );
        } else if (mode === 'commuters_affected') {
          const val = data.commuters_affected != null ? Number(data.commuters_affected) : 0;
          dataValues.push(val);
          dataHoverTexts.push(
            `Ward Name: ${wardName}<br>Commuters Affected: ${val.toLocaleString()}<br>Disruption Minutes: ${data.disruption_minutes != null ? data.disruption_minutes.toFixed(0) : 'N/A'} mins<br>Severity: ${displaySeverity}`
          );
        } else {
          const val = data.disruption_minutes != null ? Number(data.disruption_minutes) : 0;
          dataValues.push(val);
          dataHoverTexts.push(
            `Ward Name: ${wardName}<br>Disruption Minutes: ${val.toFixed(0)} mins<br>Commuters Affected: ${(data.commuters_affected != null ? data.commuters_affected.toLocaleString() : 'N/A')}<br>Severity: ${displaySeverity}`
          );
        }
      } else {
        noDataWardNumbers.push(wardNum);
        noDataHoverTexts.push(`Ward Name: ${wardName}<br>No transport disruption data.`);
      }
    });

    setWardDataMap(newWardDataMap);
    if (mode === 'severity') setSeverityCounts(counts);

    let colorscale, colorbarTitle, zmin, zmax, tickvals, ticktext;
    if (mode === 'severity') {
      colorscale = [[0, '#2E7D32'], [0.5, '#F9A825'], [1, '#B71C1C']];
      colorbarTitle = 'Severity Level';
      zmin = 1;
      zmax = 3;
      tickvals = [1, 2, 3];
      ticktext = ['LOW', 'MEDIUM', 'HIGH'];
    } else if (mode === 'commuters_affected') {
      colorscale = [[0, '#f7fbff'], [0.25, '#c6dbef'], [0.5, '#6baed6'], [0.75, '#2171b5'], [1, '#08306b']];
      colorbarTitle = 'Commuters Affected';
      zmin = dataValues.length ? Math.min(...dataValues) : 0;
      zmax = dataValues.length ? Math.max(...dataValues) : 1;
    } else {
      colorscale = [[0, '#ffffcc'], [0.25, '#ffeda0'], [0.5, '#feb24c'], [0.75, '#f03b20'], [1, '#bd0026']];
      colorbarTitle = 'Disruption Minutes';
      zmin = dataValues.length ? Math.min(...dataValues) : 0;
      zmax = dataValues.length ? Math.max(...dataValues) : 1;
    }

    const traces = [];
    if (dataWardNumbers.length > 0) {
      traces.push({
        type: 'choroplethmapbox',
        geojson: geojson,
        locations: dataWardNumbers,
        z: dataValues,
        featureidkey: featureIdKey,
        text: dataHoverTexts,
        hoverinfo: 'text',
        colorscale: colorscale,
        zmin: zmin,
        zmax: zmax,
        marker: { opacity: 0.7, line: { width: 1.5, color: '#000000' } },
        colorbar: mode === 'severity' ? undefined : { title: { text: colorbarTitle, side: 'right' }, thickness: 15, len: 0.7, x: 1.02, tickvals: tickvals, ticktext: ticktext },
        showscale: mode !== 'severity'
      });
    }
    if (noDataWardNumbers.length > 0) {
      traces.push({
        type: 'choroplethmapbox',
        geojson: geojson,
        locations: noDataWardNumbers,
        z: new Array(noDataWardNumbers.length).fill(0),
        featureidkey: featureIdKey,
        text: noDataHoverTexts,
        hoverinfo: 'text',
        colorscale: [[0, '#808080'], [1, '#404040']],
        zmin: 0,
        zmax: 1,
        marker: { opacity: 0.7, line: { width: 1.5, color: '#000000' } },
        showscale: false
      });
    }
    if (selectedWard && selectedWard.id) {
      traces.push({
        name: 'selected-ward-highlight',
        type: 'choroplethmapbox',
        geojson: geojson,
        locations: [selectedWard.id],
        z: [1],
        featureidkey: featureIdKey,
        hoverinfo: 'skip',
        colorscale: [[0, 'rgba(255, 215, 0, 0.25)'], [1, 'rgba(255, 215, 0, 0.25)']],
        zmin: 0,
        zmax: 1,
        marker: { opacity: 0.35, line: { width: 5, color: '#FFD700' } },
        showscale: false
      });
    }
    addIndiaBoundaryTrace(traces);
    setPlotData(traces);
    setPlotLayout({
      mapbox: { style: 'open-street-map', center: mapCenter, zoom: FIXED_ZOOM_LEVEL, bearing: 0, pitch: 0 },
      margin: {
        t: 0, b: 0, l: 0,
        r: mode === 'severity' ? 0 : (window.innerWidth <= 480 ? 20 : (window.innerWidth <= 768 ? 40 : 50))
      },
      height: window.innerWidth <= 480 ? 300 : (window.innerWidth <= 768 ? 400 : 500),
      autosize: true,
      dragmode: 'zoom',
      clickmode: 'event',
      uirevision: 'constant',
      hovermode: 'closest',
      hoverdistance: 5,
      hoverlabel: { bgcolor: 'white', bordercolor: '#333', font: { size: 12 }, namelength: -1 }
    });
  }, [setWardDataMap, setSeverityCounts, selectedWard, selectedDate, startDate, endDate, getIndicatorThreshold, indiaGeoJSON, city]);

  // Helper: build date range strings from startDate/endDate/selectedDate
  const getDateRangeStrings = useCallback(() => {
    let startDateStr = '2025-05-01';
    let endDateStr = '2025-05-31';
    if (startDate && endDate) {
      startDateStr = `${startDate.getFullYear()}-${String(startDate.getMonth() + 1).padStart(2, '0')}-${String(startDate.getDate()).padStart(2, '0')}`;
      endDateStr = `${endDate.getFullYear()}-${String(endDate.getMonth() + 1).padStart(2, '0')}-${String(endDate.getDate()).padStart(2, '0')}`;
    } else if (selectedDate) {
      const y = selectedDate.getFullYear();
      const m = selectedDate.getMonth() + 1;
      startDateStr = `${y}-${String(m).padStart(2, '0')}-01`;
      const lastDay = new Date(y, m, 0).getDate();
      endDateStr = `${y}-${String(m).padStart(2, '0')}-${lastDay}`;
    }
    return { startDateStr, endDateStr };
  }, [startDate, endDate, selectedDate]);

  // Render map for indicators with severity (and optional param) tabs - generic helper
  const renderSeverityParamMap = useCallback(async (geojson, mapCenter, mapZoom, mode, { fetchFn, indicatorName, noDataLabel, paramModes = [] }) => {
    const { startDateStr, endDateStr } = getDateRangeStrings();
    const threshold = getIndicatorThreshold(indicatorName);
    let wardData = {};
    try {
      wardData = await fetchFn(startDateStr, endDateStr, threshold, city);
    } catch (apiError) {
      console.error(`API error fetching ${indicatorName}:`, apiError);
      throw new Error(`Failed to fetch data: ${apiError.message}`);
    }
    if (!wardData || typeof wardData !== 'object') wardData = {};

    const wardPropertyName = getWardPropertyName(geojson);
    const featureIdKey = `properties.${wardPropertyName}`;
    const dataWardNumbers = [];
    const dataValues = [];
    const dataHoverTexts = [];
    const noDataWardNumbers = [];
    const noDataHoverTexts = [];
    const newWardDataMap = {};
    const counts = { LOW: 0, MEDIUM: 0, HIGH: 0 };

    const paramMode = paramModes.find(p => p.id === mode);
    const isSeverityMode = mode === 'severity' || !paramMode;

    geojson.features.forEach((feature) => {
      const wardNum = feature.properties[wardPropertyName];
      const wardName = feature.properties.ward_name || feature.properties.Ward_Name || `Ward ${wardNum}`;
      let numericWardNum = null;
      if (typeof wardNum === 'number') numericWardNum = wardNum;
      else if (typeof wardNum === 'string') { const parsed = parseInt(wardNum, 10); if (!isNaN(parsed)) numericWardNum = parsed; }
      const data = numericWardNum != null ? wardData[numericWardNum] : null;

      newWardDataMap[wardNum] = { id: wardNum, name: wardName, properties: feature.properties, geometry: feature.geometry, data, numericWardNum };

      if (data) {
        dataWardNumbers.push(wardNum);
        const displaySeverity = (data.dominant_severity || 'LOW').toUpperCase();
        if (isSeverityMode) {
          const severityMap = { LOW: 1, MEDIUM: 2, HIGH: 3 };
          dataValues.push(severityMap[displaySeverity] || 1);
          counts[displaySeverity] = (counts[displaySeverity] || 0) + 1;
          dataHoverTexts.push(`Ward: ${wardName}<br>Severity: ${displaySeverity}`);
        } else {
          const val = paramMode.field != null && data[paramMode.field] != null ? Number(data[paramMode.field]) : 0;
          dataValues.push(val);
          dataHoverTexts.push(`Ward: ${wardName}<br>${paramMode.colorbarTitle || paramMode.id}: ${typeof val === 'number' && (val % 1 !== 0) ? val.toFixed(2) : val}<br>Severity: ${displaySeverity}`);
        }
      } else {
        noDataWardNumbers.push(wardNum);
        noDataHoverTexts.push(`Ward: ${wardName}<br>${noDataLabel}`);
      }
    });

    setWardDataMap(newWardDataMap);
    if (isSeverityMode) setSeverityCounts(counts);

    let colorscale, colorbarTitle, zmin, zmax, tickvals, ticktext;
    if (isSeverityMode) {
      colorscale = [[0, '#2E7D32'], [0.5, '#F9A825'], [1, '#B71C1C']];
      colorbarTitle = 'Severity Level';
      zmin = 1;
      zmax = 3;
      tickvals = [1, 2, 3];
      ticktext = ['LOW', 'MEDIUM', 'HIGH'];
    } else {
      colorbarTitle = paramMode.colorbarTitle || paramMode.id;
      colorscale = paramMode.colorscale || [[0, '#f7fbff'], [0.5, '#6baed6'], [1, '#08306b']];
      zmin = dataValues.length ? Math.min(...dataValues) : 0;
      zmax = dataValues.length ? Math.max(...dataValues) : 1;
    }

    const traces = [];
    if (dataWardNumbers.length > 0) {
      traces.push({
        type: 'choroplethmapbox',
        geojson, locations: dataWardNumbers, z: dataValues, featureidkey: featureIdKey,
        text: dataHoverTexts, hoverinfo: 'text', colorscale, zmin, zmax,
        marker: { opacity: 0.7, line: { width: 1.5, color: '#000000' } },
        colorbar: isSeverityMode ? undefined : { title: { text: colorbarTitle, side: 'right' }, thickness: 15, len: 0.7, x: 1.02, tickvals: tickvals, ticktext: ticktext },
        showscale: !isSeverityMode
      });
    }
    if (noDataWardNumbers.length > 0) {
      traces.push({
        type: 'choroplethmapbox', geojson, locations: noDataWardNumbers,
        z: new Array(noDataWardNumbers.length).fill(0), featureidkey: featureIdKey,
        text: noDataHoverTexts, hoverinfo: 'text',
        colorscale: [[0, '#808080'], [1, '#404040']], zmin: 0, zmax: 1,
        marker: { opacity: 0.7, line: { width: 1.5, color: '#000000' } }, showscale: false
      });
    }
    if (selectedWard && selectedWard.id) {
      traces.push({
        name: 'selected-ward-highlight', type: 'choroplethmapbox', geojson, locations: [selectedWard.id], z: [1],
        featureidkey: featureIdKey, hoverinfo: 'skip',
        colorscale: [[0, 'rgba(255, 215, 0, 0.25)'], [1, 'rgba(255, 215, 0, 0.25)']], zmin: 0, zmax: 1,
        marker: { opacity: 0.35, line: { width: 5, color: '#FFD700' } }, showscale: false
      });
    }
    addIndiaBoundaryTrace(traces);
    setPlotData(traces);
    setPlotLayout({
      mapbox: { style: 'open-street-map', center: mapCenter, zoom: FIXED_ZOOM_LEVEL, bearing: 0, pitch: 0 },
      margin: { t: 0, b: 0, l: 0, r: isSeverityMode ? 0 : (window.innerWidth <= 480 ? 20 : (window.innerWidth <= 768 ? 40 : 50)) },
      height: window.innerWidth <= 480 ? 300 : (window.innerWidth <= 768 ? 400 : 500),
      autosize: true, dragmode: 'zoom', clickmode: 'event', uirevision: 'constant',
      hovermode: 'closest', hoverdistance: 5, hoverlabel: { bgcolor: 'white', bordercolor: '#333', font: { size: 12 }, namelength: -1 }
    });
  }, [getDateRangeStrings, getIndicatorThreshold, city, setWardDataMap, setSeverityCounts, selectedWard, indiaGeoJSON]);

  // Render groundwater map
  const renderGroundwaterMap = useCallback(async (geojson, mapCenter, mapZoom, mode) => {
    let startDateStr = '2020-01-01';
    let endDateStr = '2025-12-31';
    if (startDate && endDate) {
      startDateStr = `${startDate.getFullYear()}-${String(startDate.getMonth() + 1).padStart(2, '0')}-${String(startDate.getDate()).padStart(2, '0')}`;
      endDateStr = `${endDate.getFullYear()}-${String(endDate.getMonth() + 1).padStart(2, '0')}-${String(endDate.getDate()).padStart(2, '0')}`;
    } else if (selectedDate) {
      const y = selectedDate.getFullYear();
      const m = selectedDate.getMonth() + 1;
      startDateStr = `${y}-${String(m).padStart(2, '0')}-01`;
      const lastDay = new Date(y, m, 0).getDate();
      endDateStr = `${y}-${String(m).padStart(2, '0')}-${lastDay}`;
    }
    const threshold = getIndicatorThreshold(groundwaterIndicatorName);
    let rawWardData;
    try {
      rawWardData = await fetchGroundwaterDataForWards(startDateStr, endDateStr, threshold);
    } catch (apiError) {
      console.error('API error fetching groundwater data:', apiError);
      throw new Error(`Failed to fetch groundwater data: ${apiError.message}`);
    }
    if (!rawWardData || typeof rawWardData !== 'object') {
      rawWardData = {};
    }
    // When API returns no records for the period, we still render the map with all wards in "no data" state (grey)

    const wardPropertyName = getWardPropertyName(geojson);
    const featureIdKey = `properties.${wardPropertyName}`;
    const dataWardNumbers = [];
    const dataValues = [];
    const dataHoverTexts = [];
    const noDataWardNumbers = [];
    const noDataHoverTexts = [];
    const newWardDataMap = {};

    geojson.features.forEach((feature) => {
      const locationId = feature.properties[wardPropertyName];
      const wardName = feature.properties.ward_name || feature.properties.Ward_Name || feature.properties.ward_name || `Ward ${locationId}`;
      const data = rawWardData[locationId] ?? rawWardData[wardName] ?? null;

      newWardDataMap[locationId] = {
        id: locationId,
        name: wardName,
        properties: feature.properties,
        geometry: feature.geometry,
        data,
        numericWardNum: typeof locationId === 'number' ? locationId : parseInt(locationId, 10)
      };

      if (data) {
        dataWardNumbers.push(locationId);
        const dep = data.latest_depletion_level_meters != null ? Number(data.latest_depletion_level_meters) : 0;
        const gwl = data.latest_current_gwl_meters != null ? Number(data.latest_current_gwl_meters) : null;
        const pct = data.latest_severity_index != null ? Number(data.latest_severity_index) : null;
        const displaySeverity = data.dominant_severity || 'LOW';

        if (mode === 'severity') {
          const severityMap = { LOW: 1, MEDIUM: 2, HIGH: 3 };
          dataValues.push(severityMap[displaySeverity] || 1);
          dataHoverTexts.push(
            `Ward: ${wardName}<br>Depletion: ${dep.toFixed(2)} m<br>Severity: ${displaySeverity}`
          );
        } else if (mode === 'gwl') {
          const val = gwl != null && !isNaN(gwl) ? gwl : 0;
          dataValues.push(val);
          dataHoverTexts.push(
            `Ward: ${wardName}<br>Current GWL: ${val.toFixed(2)} m`
          );
        } else {
          const val = pct != null && !isNaN(pct) ? pct : 0;
          dataValues.push(val);
          dataHoverTexts.push(
            `Ward: ${wardName}<br>Depletion %: ${val.toFixed(1)}%`
          );
        }
      } else {
        noDataWardNumbers.push(locationId);
        noDataHoverTexts.push(`Ward: ${wardName}<br>No data`);
      }
    });

    setWardDataMap(newWardDataMap);

    let colorscale, colorbarTitle, zmin, zmax, tickvals, ticktext;
    if (mode === 'severity') {
      colorscale = [[0, '#2E7D32'], [0.5, '#F9A825'], [1, '#B71C1C']];
      colorbarTitle = 'Severity Level';
      zmin = 1;
      zmax = 3;
      tickvals = [1, 2, 3];
      ticktext = ['LOW', 'MEDIUM', 'HIGH'];
    } else if (mode === 'gwl') {
      colorscale = [[0, '#08306b'], [0.25, '#2171b5'], [0.5, '#6baed6'], [0.75, '#c6dbef'], [1, '#f7fbff']];
      colorbarTitle = 'Current GWL (m)';
      zmin = dataValues.length ? Math.min(...dataValues) : 0;
      zmax = dataValues.length ? Math.max(...dataValues) : 1;
    } else {
      colorscale = [[0, '#ffffcc'], [0.25, '#ffeda0'], [0.5, '#feb24c'], [0.75, '#f03b20'], [1, '#bd0026']];
      colorbarTitle = 'Depletion %';
      zmin = dataValues.length ? Math.min(...dataValues) : 0;
      zmax = dataValues.length ? Math.max(...dataValues) : 100;
    }

    const traces = [];
    if (dataWardNumbers.length > 0) {
      traces.push({
        type: 'choroplethmapbox',
        geojson,
        locations: dataWardNumbers,
        z: dataValues,
        featureidkey: featureIdKey,
        text: dataHoverTexts,
        hoverinfo: 'text',
        colorscale,
        zmin,
        zmax,
        marker: { opacity: 0.7, line: { width: 1.5, color: '#000000' } },
        colorbar: mode === 'severity' ? undefined : {
          title: { text: colorbarTitle, side: 'right' },
          thickness: 15,
          len: 0.7,
          x: 1.02,
          tickvals: mode === 'severity' ? tickvals : undefined,
          ticktext: mode === 'severity' ? ticktext : undefined
        },
        showscale: mode !== 'severity'
      });
    }
    if (noDataWardNumbers.length > 0) {
      traces.push({
        type: 'choroplethmapbox',
        geojson,
        locations: noDataWardNumbers,
        z: new Array(noDataWardNumbers.length).fill(0),
        featureidkey: featureIdKey,
        text: noDataHoverTexts,
        hoverinfo: 'text',
        colorscale: [[0, '#808080'], [1, '#404040']],
        zmin: 0,
        zmax: 1,
        marker: { opacity: 0.7, line: { width: 1.5, color: '#000000' } },
        showscale: false
      });
    }
    if (selectedWard && selectedWard.id != null) {
      traces.push({
        name: 'selected-ward-highlight',
        type: 'choroplethmapbox',
        geojson,
        locations: [selectedWard.id],
        z: [1],
        featureidkey: featureIdKey,
        hoverinfo: 'skip',
        colorscale: [[0, 'rgba(255, 215, 0, 0.25)'], [1, 'rgba(255, 215, 0, 0.25)']],
        zmin: 0,
        zmax: 1,
        marker: { opacity: 0.35, line: { width: 5, color: '#FFD700' } },
        showscale: false
      });
    }

    addIndiaBoundaryTrace(traces);
    setPlotData(traces);
    const selectedCenter = selectedWard?.geometry ? getGeometryBboxCenter(selectedWard.geometry) : null;
    const effectiveCenter = selectedCenter ? { lat: selectedCenter.lat, lon: selectedCenter.lon } : mapCenter;
    setPlotLayout({
      mapbox: { style: 'open-street-map', center: effectiveCenter, zoom: FIXED_ZOOM_LEVEL, bearing: 0, pitch: 0 },
      margin: {
        t: 0, b: 0, l: 0,
        r: mode === 'severity' ? 0 : (window.innerWidth <= 480 ? 20 : (window.innerWidth <= 768 ? 40 : 50))
      },
      height: window.innerWidth <= 480 ? 300 : (window.innerWidth <= 768 ? 400 : 500),
      autosize: true,
      dragmode: 'zoom',
      clickmode: 'event',
      uirevision: 'constant',
      hovermode: 'closest',
      hoverdistance: 5,
      hoverlabel: { bgcolor: 'white', bordercolor: '#333', font: { size: 12 }, namelength: -1 }
    });
  }, [setWardDataMap, selectedWard, startDate, endDate, selectedDate, getIndicatorThreshold, indiaGeoJSON, getGeometryBboxCenter]);

  // Render informal workers map
  const renderInformalWorkersMap = useCallback(async (geojson, mapCenter, mapZoom, mode, indicatorType) => {
    console.log('=== renderInformalWorkersMap called ===', { mode, indicatorType });
    
    // Calculate date range from selectedDate or use startDate/endDate
    let startDateStr = '2026-01-01';
    let endDateStr = '2026-02-04';
    
    if (startDate && endDate) {
      startDateStr = `${startDate.getFullYear()}-${String(startDate.getMonth() + 1).padStart(2, '0')}-${String(startDate.getDate()).padStart(2, '0')}`;
      endDateStr = `${endDate.getFullYear()}-${String(endDate.getMonth() + 1).padStart(2, '0')}-${String(endDate.getDate()).padStart(2, '0')}`;
    } else if (selectedDate) {
      const year = selectedDate.getFullYear();
      const month = selectedDate.getMonth() + 1;
      startDateStr = `${year}-${String(month).padStart(2, '0')}-01`;
      const lastDay = new Date(year, month, 0).getDate();
      endDateStr = `${year}-${String(month).padStart(2, '0')}-${lastDay}`;
    }
    
    console.log(`Fetching informal workers data from ${startDateStr} to ${endDateStr}`);
    
    let informalWorkersData = {};
    try {
      informalWorkersData = await fetchInformalWorkersDataForWards(startDateStr, endDateStr, indicatorType, city);
      console.log('Informal workers data received:', Object.keys(informalWorkersData).length, 'wards');
    } catch (apiError) {
      console.error('API error fetching informal workers data:', apiError);
      // Don't throw - continue to render map with all wards as "no data"
      informalWorkersData = {};
    }
    
    // If no data returned, we'll render all wards as "no data" (grey) - don't throw error
    if (!informalWorkersData || Object.keys(informalWorkersData).length === 0) {
      console.warn(`Informal workers data is not available for this period (${startDateStr} to ${endDateStr}). Rendering map with all wards as "no data".`);
      // Continue to render map with all wards showing as "no data"
    }

    const wardPropertyName = getWardPropertyName(geojson);
    const featureIdKey = `properties.${wardPropertyName}`;

    const dataWardNumbers = [];
    const dataValues = [];
    const dataHoverTexts = [];
    const noDataWardNumbers = [];
    const noDataHoverTexts = [];
    const newWardDataMap = {};
    const counts = { LOW: 0, MEDIUM: 0, HIGH: 0, NO_DATA: 0 };

    geojson.features.forEach((feature) => {
      const wardNum = feature.properties[wardPropertyName];
      const wardName = feature.properties.ward_name || feature.properties.Ward_Name || `Ward ${wardNum}`;
      
      let numericWardNum = null;
      if (typeof wardNum === 'number') {
        numericWardNum = wardNum;
      } else if (typeof wardNum === 'string') {
        const parsed = parseInt(wardNum, 10);
        if (!isNaN(parsed)) {
          numericWardNum = parsed;
        }
      }
      
      const data = numericWardNum !== null ? informalWorkersData[numericWardNum] : null;

      newWardDataMap[wardNum] = {
        id: wardNum,
        name: wardName,
        properties: feature.properties,
        geometry: feature.geometry,
        data: data,
        numericWardNum: numericWardNum
      };

      if (data) {
        dataWardNumbers.push(wardNum);

        if (mode === 'severity') {
          // Determine which severity field to use based on indicator type
          const severityPercentage = indicatorType === 'no_show_rate' 
            ? data.no_show_rate_severity 
            : data.informal_income_loss_severity;
          
          const severityLevel = severityPercentage !== null && severityPercentage !== undefined
            ? calculateInformalWorkersSeverityLevel(severityPercentage)
            : 'LOW';
          
          const severityMap = { LOW: 1, MEDIUM: 2, HIGH: 3 };
          dataValues.push(severityMap[severityLevel] || 1);
          
          // Count wards by severity level
          if (severityLevel === 'LOW') counts.LOW++;
          else if (severityLevel === 'MEDIUM') counts.MEDIUM++;
          else if (severityLevel === 'HIGH') counts.HIGH++;
          
          const severityLabel = indicatorType === 'no_show_rate' 
            ? 'No Show Rate' 
            : 'Daily Wage Loss';
          const percentageValue = severityPercentage !== null && severityPercentage !== undefined 
            ? severityPercentage.toFixed(1) 
            : 'N/A';
          
          dataHoverTexts.push(
            `Ward Name: ${wardName}<br>` +
            `${severityLabel} Severity: ${severityLevel} (${percentageValue}%)<br>` +
            `Total Registered Workers: ${data.total_registered_e_shram?.toLocaleString() || 'N/A'}<br>` +
            `Total Heat Illness: ${data.total_heat_illness?.toLocaleString() || 'N/A'}`
          );
        } else if (mode === 'total_registered') {
          const value = data.total_registered_e_shram !== null && data.total_registered_e_shram !== undefined
            ? Number(data.total_registered_e_shram)
            : 0;
          dataValues.push(value);
          dataHoverTexts.push(
            `Ward Name: ${wardName}<br>` +
            `Total Registered Workers: ${value.toLocaleString()}<br>` +
            `Total Heat Illness: ${data.total_heat_illness?.toLocaleString() || 'N/A'}`
          );
        } else if (mode === 'total_heat_illness') {
          const value = data.total_heat_illness !== null && data.total_heat_illness !== undefined
            ? Number(data.total_heat_illness)
            : 0;
          dataValues.push(value);
          dataHoverTexts.push(
            `Ward Name: ${wardName}<br>` +
            `Total Heat Illness: ${value.toLocaleString()}<br>` +
            `Total Registered Workers: ${data.total_registered_e_shram?.toLocaleString() || 'N/A'}`
          );
        }
      } else {
        noDataWardNumbers.push(wardNum);
        counts.NO_DATA++;
        noDataHoverTexts.push(`Ward Name: ${wardName}<br>No data available`);
      }
    });

    setWardDataMap(newWardDataMap);
    setSeverityCounts(counts);

    let colorscale, colorbarTitle, zmin, zmax, tickvals, ticktext;
    
    if (mode === 'severity') {
      colorscale = [[0, '#2E7D32'], [0.5, '#F9A825'], [1, '#B71C1C']];
      colorbarTitle = 'Severity Level';
      zmin = 1;
      zmax = 3;
      tickvals = [1, 2, 3];
      ticktext = ['LOW', 'MEDIUM', 'HIGH'];
    } else if (mode === 'total_registered') {
      colorscale = [[0, '#f7fbff'], [0.25, '#c6dbef'], [0.5, '#6baed6'], [0.75, '#2171b5'], [1, '#08306b']];
      colorbarTitle = 'Total Registered Workers';
      zmin = dataValues.length > 0 ? Math.min(...dataValues) : 0;
      zmax = dataValues.length > 0 ? Math.max(...dataValues) : 1;
    } else {
      colorscale = [[0, '#ffffcc'], [0.25, '#ffeda0'], [0.5, '#feb24c'], [0.75, '#f03b20'], [1, '#bd0026']];
      colorbarTitle = 'Total Heat Illness';
      zmin = dataValues.length > 0 ? Math.min(...dataValues) : 0;
      zmax = dataValues.length > 0 ? Math.max(...dataValues) : 1;
    }

    const traces = [];

    // Always add wards with data first (if any)
    if (dataWardNumbers.length > 0) {
      traces.push({
        type: 'choroplethmapbox',
        geojson: geojson,
        locations: dataWardNumbers,
        z: dataValues,
        featureidkey: featureIdKey,
        text: dataHoverTexts,
        hoverinfo: 'text',
        colorscale: colorscale,
        zmin: zmin,
        zmax: zmax,
        marker: { opacity: 0.7, line: { width: 1.5, color: '#000000' } },
        colorbar: mode === 'severity' ? undefined : {
          title: { text: colorbarTitle, side: 'right' },
          thickness: 15,
          len: 0.7,
          x: 1.02,
          tickvals: tickvals,
          ticktext: ticktext
        },
        showscale: mode !== 'severity'
      });
    }

    // Always add wards with no data (grey) - ensures full city map is always visible
    // This trace is added even if all wards have no data, so the map always renders
    if (noDataWardNumbers.length > 0) {
      traces.push({
        type: 'choroplethmapbox',
        geojson: geojson,
        locations: noDataWardNumbers,
        z: new Array(noDataWardNumbers.length).fill(0),
        featureidkey: featureIdKey,
        text: noDataHoverTexts,
        hoverinfo: 'text',
        colorscale: [[0, '#808080'], [1, '#404040']],
        zmin: 0,
        zmax: 1,
        marker: { opacity: 0.7, line: { width: 1.5, color: '#000000' } },
        showscale: false
      });
    }
    
    // Safety check: if no traces were added (shouldn't happen), add a base map trace with all wards as "no data"
    if (traces.length === 0) {
      const allWardNumbers = geojson.features.map(f => f.properties[wardPropertyName]);
      traces.push({
        type: 'choroplethmapbox',
        geojson: geojson,
        locations: allWardNumbers,
        z: new Array(allWardNumbers.length).fill(0),
        featureidkey: featureIdKey,
        text: allWardNumbers.map(wn => {
          const feature = geojson.features.find(f => f.properties[wardPropertyName] === wn);
          const wardName = feature?.properties?.ward_name || feature?.properties?.Ward_Name || `Ward ${wn}`;
          return `Ward Name: ${wardName}<br>No data available`;
        }),
        hoverinfo: 'text',
        colorscale: [[0, '#808080'], [1, '#404040']],
        zmin: 0,
        zmax: 1,
        marker: { opacity: 0.7, line: { width: 1.5, color: '#000000' } },
        showscale: false
      });
    }

    // Add selected ward highlight
    if (selectedWard && selectedWard.id) {
      traces.push({
        name: 'selected-ward-highlight',
        type: 'choroplethmapbox',
        geojson: geojson,
        locations: [selectedWard.id],
        z: [1],
        featureidkey: featureIdKey,
        hoverinfo: 'skip',
        colorscale: [[0, 'rgba(255, 215, 0, 0.25)'], [1, 'rgba(255, 215, 0, 0.25)']],
        zmin: 0,
        zmax: 1,
        marker: { opacity: 0.35, line: { width: 5, color: '#FFD700' } },
        showscale: false
      });
    }

    addIndiaBoundaryTrace(traces);
    setPlotData(traces);
    setPlotLayout({
      mapbox: {
        style: 'open-street-map',
        center: mapCenter,
        zoom: FIXED_ZOOM_LEVEL,
        bearing: 0,
        pitch: 0
      },
      margin: { 
        t: 0, 
        b: 0, 
        l: 0, 
        r: mode === 'severity' ? 0 : (window.innerWidth <= 480 ? 20 : (window.innerWidth <= 768 ? 40 : 50))
      },
      height: window.innerWidth <= 480 ? 300 : (window.innerWidth <= 768 ? 400 : 500),
      autosize: true,
      dragmode: 'zoom',
      clickmode: 'event',
      uirevision: 'constant',
      hovermode: 'closest',
      hoverdistance: 5,
      hoverlabel: { bgcolor: 'white', bordercolor: '#333', font: { size: 12 }, namelength: -1 }
    });
    
    console.log('✅ Informal workers map rendering completed successfully');
  }, [setWardDataMap, selectedWard, startDate, endDate, selectedDate, indiaGeoJSON, city]);

  const renderConstructionLaborMap = useCallback(async (geojson, mapCenter, mapZoom, mode, indicatorType) => {
    console.log('=== renderConstructionLaborMap called ===', { mode, indicatorType });
    
    // Calculate date range from selectedDate or use startDate/endDate
    let startDateStr = '2025-05-01';
    let endDateStr = '2026-02-04';
    
    if (startDate && endDate) {
      startDateStr = `${startDate.getFullYear()}-${String(startDate.getMonth() + 1).padStart(2, '0')}-${String(startDate.getDate()).padStart(2, '0')}`;
      endDateStr = `${endDate.getFullYear()}-${String(endDate.getMonth() + 1).padStart(2, '0')}-${String(endDate.getDate()).padStart(2, '0')}`;
    } else if (selectedDate) {
      const year = selectedDate.getFullYear();
      const month = selectedDate.getMonth() + 1;
      startDateStr = `${year}-${String(month).padStart(2, '0')}-01`;
      const lastDay = new Date(year, month, 0).getDate();
      endDateStr = `${year}-${String(month).padStart(2, '0')}-${lastDay}`;
    }
    
    console.log(`Fetching construction labor data from ${startDateStr} to ${endDateStr}`);
    
    let constructionLaborData = {};
    try {
      constructionLaborData = await fetchConstructionLaborDataForWards(startDateStr, endDateStr, indicatorType, city);
      console.log('Construction labor data received:', Object.keys(constructionLaborData).length, 'wards');
    } catch (apiError) {
      console.error('API error fetching construction labor data:', apiError);
      constructionLaborData = {};
    }
    
    if (!constructionLaborData || Object.keys(constructionLaborData).length === 0) {
      console.warn(`Construction labor data is not available for this period (${startDateStr} to ${endDateStr}). Rendering map with all wards as "no data".`);
    }

    const wardPropertyName = getWardPropertyName(geojson);
    const featureIdKey = `properties.${wardPropertyName}`;

    const dataWardNumbers = [];
    const dataValues = [];
    const dataHoverTexts = [];
    const noDataWardNumbers = [];
    const noDataHoverTexts = [];
    const newWardDataMap = {};
    const counts = { LOW: 0, MEDIUM: 0, HIGH: 0, NO_DATA: 0 };

    geojson.features.forEach((feature) => {
      const wardNum = feature.properties[wardPropertyName];
      const wardName = feature.properties.ward_name || feature.properties.Ward_Name || `Ward ${wardNum}`;
      
      let numericWardNum = null;
      if (typeof wardNum === 'number') {
        numericWardNum = wardNum;
      } else if (typeof wardNum === 'string') {
        const parsed = parseInt(wardNum, 10);
        if (!isNaN(parsed)) {
          numericWardNum = parsed;
        }
      }
      
      const data = numericWardNum !== null ? constructionLaborData[numericWardNum] : null;

      newWardDataMap[wardNum] = {
        id: wardNum,
        name: wardName,
        properties: feature.properties,
        geometry: feature.geometry,
        data: data,
        numericWardNum: numericWardNum
      };

      if (data) {
        dataWardNumbers.push(wardNum);

        if (mode === 'severity') {
          // Determine which severity field and calculation function to use based on indicator type
          let severityPercentage = null;
          let severityLevel = 'LOW';
          let severityLabel = '';
          
          if (indicatorType === 'lost_hours') {
            severityPercentage = data.lost_hour_severity_pct;
            severityLevel = severityPercentage !== null && severityPercentage !== undefined
              ? calculateLostLaborHoursSeverityLevel(severityPercentage)
              : 'LOW';
            severityLabel = 'Lost Labor Hours';
          } else if (indicatorType === 'no_shade_water') {
            severityPercentage = data.no_shade_water_severity_pct;
            severityLevel = severityPercentage !== null && severityPercentage !== undefined
              ? calculateNoShadeWaterSeverityLevel(severityPercentage)
              : 'LOW';
            severityLabel = '% Without Shade/Water';
          } else if (indicatorType === 'heat_illness') {
            severityPercentage = data.heat_illness_severity_pct;
            severityLevel = severityPercentage !== null && severityPercentage !== undefined
              ? calculateHeatIllnessSeverityLevel(severityPercentage)
              : 'LOW';
            severityLabel = 'Heat Illness Cases';
          }
          
          const severityMap = { LOW: 1, MEDIUM: 2, HIGH: 3 };
          dataValues.push(severityMap[severityLevel] || 1);
          
          // Count wards by severity level
          if (severityLevel === 'LOW') counts.LOW++;
          else if (severityLevel === 'MEDIUM') counts.MEDIUM++;
          else if (severityLevel === 'HIGH') counts.HIGH++;
          
          const percentageValue = severityPercentage !== null && severityPercentage !== undefined 
            ? severityPercentage.toFixed(1) 
            : 'N/A';
          
          dataHoverTexts.push(
            `Ward Name: ${wardName}<br>` +
            `${severityLabel} Severity: ${severityLevel} (${percentageValue}%)<br>` +
            `Total Present Workers: ${data.total_workers_present?.toLocaleString() || 'N/A'}`
          );
        } else if (mode === 'total_present') {
          const value = data.total_workers_present !== null && data.total_workers_present !== undefined
            ? Number(data.total_workers_present)
            : 0;
          dataValues.push(value);
          dataHoverTexts.push(
            `Ward Name: ${wardName}<br>` +
            `Total Present Workers: ${value.toLocaleString()}`
          );
        }
      } else {
        noDataWardNumbers.push(wardNum);
        counts.NO_DATA++;
        noDataHoverTexts.push(`Ward Name: ${wardName}<br>No data available`);
      }
    });

    setWardDataMap(newWardDataMap);
    setSeverityCounts(counts);

    let colorscale, colorbarTitle, zmin, zmax, tickvals, ticktext;
    
    if (mode === 'severity') {
      colorscale = [[0, '#2E7D32'], [0.5, '#F9A825'], [1, '#B71C1C']];
      colorbarTitle = 'Severity Level';
      zmin = 1;
      zmax = 3;
      tickvals = [1, 2, 3];
      ticktext = ['LOW', 'MEDIUM', 'HIGH'];
    } else {
      colorscale = [[0, '#f7fbff'], [0.25, '#c6dbef'], [0.5, '#6baed6'], [0.75, '#2171b5'], [1, '#08306b']];
      colorbarTitle = 'Total Present Workers';
      zmin = dataValues.length > 0 ? Math.min(...dataValues) : 0;
      zmax = dataValues.length > 0 ? Math.max(...dataValues) : 1;
    }

    const traces = [];

    // Always add wards with data first (if any)
    if (dataWardNumbers.length > 0) {
      traces.push({
        type: 'choroplethmapbox',
        geojson: geojson,
        locations: dataWardNumbers,
        z: dataValues,
        featureidkey: featureIdKey,
        text: dataHoverTexts,
        hoverinfo: 'text',
        colorscale: colorscale,
        zmin: zmin,
        zmax: zmax,
        marker: { opacity: 0.7, line: { width: 1.5, color: '#000000' } },
        colorbar: mode === 'severity' ? undefined : {
          title: { text: colorbarTitle, side: 'right' },
          thickness: 15,
          len: 0.7,
          x: 1.02,
          tickvals: tickvals,
          ticktext: ticktext
        },
        showscale: mode !== 'severity'
      });
    }

    // Always add wards with no data (grey)
    if (noDataWardNumbers.length > 0) {
      traces.push({
        type: 'choroplethmapbox',
        geojson: geojson,
        locations: noDataWardNumbers,
        z: new Array(noDataWardNumbers.length).fill(0),
        featureidkey: featureIdKey,
        text: noDataHoverTexts,
        hoverinfo: 'text',
        colorscale: [[0, '#808080'], [1, '#404040']],
        zmin: 0,
        zmax: 1,
        marker: { opacity: 0.7, line: { width: 1.5, color: '#000000' } },
        showscale: false
      });
    }
    
    // Safety check: if no traces were added, add a base map trace with all wards as "no data"
    if (traces.length === 0) {
      const allWardNumbers = geojson.features.map(f => f.properties[wardPropertyName]);
      traces.push({
        type: 'choroplethmapbox',
        geojson: geojson,
        locations: allWardNumbers,
        z: new Array(allWardNumbers.length).fill(0),
        featureidkey: featureIdKey,
        text: allWardNumbers.map(wn => {
          const feature = geojson.features.find(f => f.properties[wardPropertyName] === wn);
          const wardName = feature?.properties?.ward_name || feature?.properties?.Ward_Name || `Ward ${wn}`;
          return `Ward Name: ${wardName}<br>No data available`;
        }),
        hoverinfo: 'text',
        colorscale: [[0, '#808080'], [1, '#404040']],
        zmin: 0,
        zmax: 1,
        marker: { opacity: 0.7, line: { width: 1.5, color: '#000000' } },
        showscale: false
      });
    }

    // Add selected ward highlight
    if (selectedWard && selectedWard.id) {
      traces.push({
        name: 'selected-ward-highlight',
        type: 'choroplethmapbox',
        geojson: geojson,
        locations: [selectedWard.id],
        z: [1],
        featureidkey: featureIdKey,
        hoverinfo: 'skip',
        colorscale: [[0, 'rgba(255, 215, 0, 0.25)'], [1, 'rgba(255, 215, 0, 0.25)']],
        zmin: 0,
        zmax: 1,
        marker: { opacity: 0.35, line: { width: 5, color: '#FFD700' } },
        showscale: false
      });
    }

    addIndiaBoundaryTrace(traces);
    setPlotData(traces);
    setPlotLayout({
      mapbox: {
        style: 'open-street-map',
        center: mapCenter,
        zoom: FIXED_ZOOM_LEVEL,
        bearing: 0,
        pitch: 0
      },
      margin: { 
        t: 0, 
        b: 0, 
        l: 0, 
        r: mode === 'severity' ? 0 : (window.innerWidth <= 480 ? 20 : (window.innerWidth <= 768 ? 40 : 50))
      },
      height: window.innerWidth <= 480 ? 300 : (window.innerWidth <= 768 ? 400 : 500),
      autosize: true,
      dragmode: 'zoom',
      clickmode: 'event',
      uirevision: 'constant',
      hovermode: 'closest',
      hoverdistance: 5,
      hoverlabel: { bgcolor: 'white', bordercolor: '#333', font: { size: 12 }, namelength: -1 }
    });
    
    console.log('✅ Construction labor map rendering completed successfully');
  }, [setWardDataMap, selectedWard, startDate, endDate, selectedDate, indiaGeoJSON, city]);

  // Render informal workers forecast map
  const renderInformalWorkersForecastMap = useCallback(async (geojson, mapCenter, mapZoom, forecastDate, indicatorType) => {
    console.log('=== renderInformalWorkersForecastMap called ===', { indicatorType });
    
    // Format date for API (YYYY-MM-DD)
    const formatDateForAPI = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };
    
    // Always fetch from today to get full 7-day forecast
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const startDate = formatDateForAPI(today);
    
    // Calculate day offset for the selected forecast day
    const selectedDate = new Date(forecastDate);
    selectedDate.setHours(0, 0, 0, 0);
    const dayOffset = Math.floor((selectedDate - today) / (1000 * 60 * 60 * 24));
    
    console.log(`🗓️ Today: ${today.toDateString()}, Selected: ${selectedDate.toDateString()}, Day Offset: ${dayOffset}`);
    
    let forecastData;
    let rawForecastData;
    let effectiveDayOffset = dayOffset;
    
    try {
      rawForecastData = await fetchInformalWorkersForecast({ start_date: startDate, days: 7, lookback_years: 5, city });
      console.log('Raw informal workers forecast data received:', rawForecastData);
      
      if (rawForecastData.days) {
        const availableDaysCount = rawForecastData.days;
        console.log('Available forecast days:', availableDaysCount);
        setForecastDaysAvailable(availableDaysCount);
        if (onForecastDaysAvailable) {
          onForecastDaysAvailable(availableDaysCount);
        }
      }
      
      if (dayOffset < 0 || dayOffset >= (rawForecastData.days || 7)) {
        throw new Error(`Day offset ${dayOffset} is outside forecast range (0-${(rawForecastData.days || 7) - 1})`);
      }
      
      forecastData = {};
      if (rawForecastData.ward_predictions && Array.isArray(rawForecastData.ward_predictions)) {
        let maxPredictionLength = 0;
        rawForecastData.ward_predictions.forEach((wardData) => {
          if (wardData.predictions && wardData.predictions.length > maxPredictionLength) {
            maxPredictionLength = wardData.predictions.length;
          }
        });
        
        if (dayOffset >= maxPredictionLength) {
          effectiveDayOffset = Math.max(0, maxPredictionLength - 1);
          console.warn(`Day offset ${dayOffset} exceeds available predictions (${maxPredictionLength}). Using fallback offset ${effectiveDayOffset}`);
        } else {
          effectiveDayOffset = dayOffset;
        }
        
        rawForecastData.ward_predictions.forEach((wardData) => {
          const wardNum = wardData.ward_number;
          const prediction = wardData.predictions && wardData.predictions[effectiveDayOffset];
          
          if (prediction && prediction.predicted_severity_index != null) {
            forecastData[wardNum] = {
              predicted_severity_index: prediction.predicted_severity_index,
              forecast_temp_c: prediction.forecast_temp_c || null,
              confidence: prediction.confidence || 'LOW',
              date: prediction.date
            };
          }
        });
      }
    } catch (apiError) {
      console.error('API error fetching informal workers forecast data:', apiError);
      throw new Error(`Failed to fetch forecast data: ${apiError.message}`);
    }
    
    if (!forecastData || Object.keys(forecastData).length === 0) {
      throw new Error(`No forecast data available for date offset ${dayOffset}`);
    }

    const wardPropertyName = getWardPropertyName(geojson);
    const featureIdKey = `properties.${wardPropertyName}`;

    const dataWardNumbers = [];
    const dataValues = [];
    const dataHoverTexts = [];
    const noDataWardNumbers = [];
    const noDataHoverTexts = [];
    const newWardDataMap = {};
    const counts = { LOW: 0, MEDIUM: 0, HIGH: 0, NO_DATA: 0 };

    // Get threshold for severity calculation
    const threshold = getIndicatorThreshold(indicatorType === 'no_show_rate' 
      ? 'No show rate among informal workers' 
      : 'Daily wage loss (street vendors, rickshaw pullers)');

    geojson.features.forEach((feature) => {
      const wardNum = feature.properties[wardPropertyName];
      const wardName = feature.properties.ward_name || feature.properties.Ward_Name || `Ward ${wardNum}`;
      
      let numericWardNum = null;
      if (typeof wardNum === 'number') {
        numericWardNum = wardNum;
      } else if (typeof wardNum === 'string') {
        const parsed = parseInt(wardNum, 10);
        if (!isNaN(parsed)) {
          numericWardNum = parsed;
        }
      }
      
      const data = numericWardNum !== null ? forecastData[numericWardNum] : null;

      newWardDataMap[wardNum] = {
        id: wardNum,
        name: wardName,
        properties: feature.properties,
        geometry: feature.geometry,
        data: data,
        numericWardNum: numericWardNum
      };

      if (data && data.predicted_severity_index != null) {
        dataWardNumbers.push(wardNum);
        const severityIndex = data.predicted_severity_index;
        const severityLevel = calculateInformalWorkersSeverityLevel(severityIndex);
        
        if (severityLevel === 'LOW') counts.LOW++;
        else if (severityLevel === 'MEDIUM') counts.MEDIUM++;
        else if (severityLevel === 'HIGH') counts.HIGH++;
        
        const severityMap = { 'LOW': 1, 'MEDIUM': 2, 'HIGH': 3 };
        const severityValue = severityMap[severityLevel] || 1;
        
        const severityColors = {
          'HIGH': '#B71C1C',
          'MEDIUM': '#F9A825',
          'LOW': '#2E7D32',
          'N/A': '#808080'
        };
        const severityColor = severityColors[severityLevel] || '#808080';
        
        dataValues.push(severityValue);
        const tempStr = data.forecast_temp_c != null ? `${data.forecast_temp_c.toFixed(1)}°C` : 'N/A';
        
        dataHoverTexts.push(
          `Ward Name: ${wardName} (${wardNum})<br>` +
          `<span style="color: ${severityColor}; font-weight: bold;">Severity: ${severityLevel}</span><br>` +
          `Predicted Severity Index: ${severityIndex != null ? severityIndex.toFixed(2) : 'N/A'}<br>` +
          `Forecast Temperature: ${tempStr}`
        );
      } else {
        noDataWardNumbers.push(wardNum);
        counts.NO_DATA++;
        noDataHoverTexts.push(`Ward Name: ${wardName} (${wardNum})<br>No Forecast data available for this ward`);
      }
    });

    setWardDataMap(newWardDataMap);
    setSeverityCounts(counts);

    const colorscale = [[0, '#2E7D32'], [0.5, '#F9A825'], [1, '#B71C1C']];
    const zmin = 1;
    const zmax = 3;

    const dataTrace = {
      type: 'choroplethmapbox',
      geojson: geojson,
      featureidkey: featureIdKey,
      locations: dataWardNumbers,
      z: dataValues,
      colorscale: colorscale,
      zmin: zmin,
      zmax: zmax,
      showscale: false,
      hoverinfo: 'text',
      hovertext: dataHoverTexts,
      marker: { 
        opacity: 0.7,
        line: { 
          color: '#000000',
          width: 1.5
        }
      },
      hoverlabel: { bgcolor: 'white', bordercolor: '#333', font: { size: 12 }, namelength: -1 }
    };

    const noDataTrace = {
      type: 'choroplethmapbox',
      geojson: geojson,
      featureidkey: featureIdKey,
      locations: noDataWardNumbers,
      z: Array(noDataWardNumbers.length).fill(0),
      colorscale: [[0, '#808080'], [1, '#404040']],
      zmin: 0,
      zmax: 1,
      showscale: false,
      hoverinfo: 'text',
      hovertext: noDataHoverTexts,
      marker: { 
        opacity: 0.7,
        line: { color: '#000000', width: 1.5 }
      },
      hoverlabel: { bgcolor: 'white', bordercolor: '#333', font: { size: 12 }, namelength: -1 }
    };

    let selectedWardTrace = null;
    if (selectedWard) {
      const selectedWardNum = selectedWard.id || selectedWard.properties?.[wardPropertyName];
      if (selectedWardNum) {
        selectedWardTrace = {
          name: 'selected-ward-highlight',
          type: 'choroplethmapbox',
          geojson: geojson,
          featureidkey: featureIdKey,
          locations: [selectedWardNum],
          z: [1],
          colorscale: [[0, 'rgba(255, 215, 0, 0.25)'], [1, 'rgba(255, 215, 0, 0.25)']],
          showscale: false,
          hoverinfo: 'skip',
          marker: { 
            opacity: 0.35,
            line: { color: '#FFD700', width: 5 }
          }
        };
      }
    }

    const traces = selectedWardTrace 
      ? [noDataTrace, dataTrace, selectedWardTrace]
      : [noDataTrace, dataTrace];

    addIndiaBoundaryTrace(traces);

    const selectedCenter = selectedWard?.geometry ? getGeometryBboxCenter(selectedWard.geometry) : null;
    const effectiveCenter = selectedCenter ? { lat: selectedCenter.lat, lon: selectedCenter.lon } : { lat: mapCenter.lat, lon: mapCenter.lon };
    const layout = {
      mapbox: {
        style: 'open-street-map',
        center: effectiveCenter,
        zoom: mapZoom
      },
      margin: { 
        t: 0, 
        b: 0, 
        l: 0, 
        r: window.innerWidth <= 480 ? 20 : (window.innerWidth <= 768 ? 40 : 50) 
      },
      showlegend: false,
      height: window.innerWidth <= 480 ? 300 : (window.innerWidth <= 768 ? 400 : 500),
      autosize: true,
      paper_bgcolor: 'rgba(0,0,0,0)',
      plot_bgcolor: 'rgba(0,0,0,0)',
      clickmode: 'event',
      uirevision: `forecast-informal-${forecastDate.getTime()}`,
      hovermode: 'closest',
      hoverdistance: 5,
      hoverlabel: { bgcolor: 'white', bordercolor: '#333', font: { size: 12 }, namelength: -1 }
    };

    setPlotData(traces);
    setPlotLayout(layout);
    setIsLoading(false);
    setError(null);
    
    console.log(`✅ Informal workers forecast map rendering completed successfully for day offset ${dayOffset}`);
  }, [indiaGeoJSON, selectedWard, setWardDataMap, getIndicatorThreshold, getGeometryBboxCenter, city, onForecastDaysAvailable, setForecastDaysAvailable]);

  // Render construction labor forecast map
  const renderConstructionLaborForecastMap = useCallback(async (geojson, mapCenter, mapZoom, forecastDate, indicatorType) => {
    console.log('=== renderConstructionLaborForecastMap called ===', { indicatorType });
    
    // Format date for API (YYYY-MM-DD)
    const formatDateForAPI = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };
    
    // Always fetch from today to get full 7-day forecast
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const startDate = formatDateForAPI(today);
    
    // Calculate day offset for the selected forecast day
    const selectedDate = new Date(forecastDate);
    selectedDate.setHours(0, 0, 0, 0);
    const dayOffset = Math.floor((selectedDate - today) / (1000 * 60 * 60 * 24));
    
    console.log(`🗓️ Today: ${today.toDateString()}, Selected: ${selectedDate.toDateString()}, Day Offset: ${dayOffset}`);
    
    let forecastData;
    let rawForecastData;
    let effectiveDayOffset = dayOffset;
    
    try {
      rawForecastData = await fetchConstructionLaborForecast({ start_date: startDate, days: 7, lookback_years: 5, city });
      console.log('Raw construction labor forecast data received:', rawForecastData);
      
      if (rawForecastData.days) {
        const availableDaysCount = rawForecastData.days;
        console.log('Available forecast days:', availableDaysCount);
        setForecastDaysAvailable(availableDaysCount);
        if (onForecastDaysAvailable) {
          onForecastDaysAvailable(availableDaysCount);
        }
      }
      
      if (dayOffset < 0 || dayOffset >= (rawForecastData.days || 7)) {
        throw new Error(`Day offset ${dayOffset} is outside forecast range (0-${(rawForecastData.days || 7) - 1})`);
      }
      
      forecastData = {};
      if (rawForecastData.ward_predictions && Array.isArray(rawForecastData.ward_predictions)) {
        let maxPredictionLength = 0;
        rawForecastData.ward_predictions.forEach((wardData) => {
          if (wardData.predictions && wardData.predictions.length > maxPredictionLength) {
            maxPredictionLength = wardData.predictions.length;
          }
        });
        
        if (dayOffset >= maxPredictionLength) {
          effectiveDayOffset = Math.max(0, maxPredictionLength - 1);
          console.warn(`Day offset ${dayOffset} exceeds available predictions (${maxPredictionLength}). Using fallback offset ${effectiveDayOffset}`);
        } else {
          effectiveDayOffset = dayOffset;
        }
        
        rawForecastData.ward_predictions.forEach((wardData) => {
          const wardNum = wardData.ward_number;
          const prediction = wardData.predictions && wardData.predictions[effectiveDayOffset];
          
          if (prediction && prediction.predicted_severity_index != null) {
            forecastData[wardNum] = {
              predicted_severity_index: prediction.predicted_severity_index,
              forecast_temp_c: prediction.forecast_temp_c || null,
              confidence: prediction.confidence || 'LOW',
              date: prediction.date
            };
          }
        });
      }
    } catch (apiError) {
      console.error('API error fetching construction labor forecast data:', apiError);
      throw new Error(`Failed to fetch forecast data: ${apiError.message}`);
    }
    
    if (!forecastData || Object.keys(forecastData).length === 0) {
      throw new Error(`No forecast data available for date offset ${dayOffset}`);
    }

    const wardPropertyName = getWardPropertyName(geojson);
    const featureIdKey = `properties.${wardPropertyName}`;

    const dataWardNumbers = [];
    const dataValues = [];
    const dataHoverTexts = [];
    const noDataWardNumbers = [];
    const noDataHoverTexts = [];
    const newWardDataMap = {};
    const counts = { LOW: 0, MEDIUM: 0, HIGH: 0, NO_DATA: 0 };

    // Determine indicator name for threshold lookup
    let indicatorNameForThreshold = 'Lost labor hours due to heat stress';
    if (indicatorType === 'no_shade_water') {
      indicatorNameForThreshold = '% of workers without access to shade/water';
    } else if (indicatorType === 'heat_illness') {
      indicatorNameForThreshold = 'Reported workplace heat illness cases';
    }
    const threshold = getIndicatorThreshold(indicatorNameForThreshold);

    geojson.features.forEach((feature) => {
      const wardNum = feature.properties[wardPropertyName];
      const wardName = feature.properties.ward_name || feature.properties.Ward_Name || `Ward ${wardNum}`;
      
      let numericWardNum = null;
      if (typeof wardNum === 'number') {
        numericWardNum = wardNum;
      } else if (typeof wardNum === 'string') {
        const parsed = parseInt(wardNum, 10);
        if (!isNaN(parsed)) {
          numericWardNum = parsed;
        }
      }
      
      const data = numericWardNum !== null ? forecastData[numericWardNum] : null;

      newWardDataMap[wardNum] = {
        id: wardNum,
        name: wardName,
        properties: feature.properties,
        geometry: feature.geometry,
        data: data,
        numericWardNum: numericWardNum
      };

      if (data && data.predicted_severity_index != null) {
        dataWardNumbers.push(wardNum);
        const severityIndex = data.predicted_severity_index;
        
        // Use appropriate severity calculation function based on indicator type
        let severityLevel = 'LOW';
        if (indicatorType === 'lost_hours') {
          severityLevel = calculateLostLaborHoursSeverityLevel(severityIndex);
        } else if (indicatorType === 'no_shade_water') {
          severityLevel = calculateNoShadeWaterSeverityLevel(severityIndex);
        } else if (indicatorType === 'heat_illness') {
          severityLevel = calculateHeatIllnessSeverityLevel(severityIndex);
        }
        
        if (severityLevel === 'LOW') counts.LOW++;
        else if (severityLevel === 'MEDIUM') counts.MEDIUM++;
        else if (severityLevel === 'HIGH') counts.HIGH++;
        
        const severityMap = { 'LOW': 1, 'MEDIUM': 2, 'HIGH': 3 };
        const severityValue = severityMap[severityLevel] || 1;
        
        const severityColors = {
          'HIGH': '#B71C1C',
          'MEDIUM': '#F9A825',
          'LOW': '#2E7D32',
          'N/A': '#808080'
        };
        const severityColor = severityColors[severityLevel] || '#808080';
        
        dataValues.push(severityValue);
        const tempStr = data.forecast_temp_c != null ? `${data.forecast_temp_c.toFixed(1)}°C` : 'N/A';
        
        dataHoverTexts.push(
          `Ward Name: ${wardName} (${wardNum})<br>` +
          `<span style="color: ${severityColor}; font-weight: bold;">Severity: ${severityLevel}</span><br>` +
          `Predicted Severity Index: ${severityIndex != null ? severityIndex.toFixed(2) : 'N/A'}<br>` +
          `Forecast Temperature: ${tempStr}`
        );
      } else {
        noDataWardNumbers.push(wardNum);
        counts.NO_DATA++;
        noDataHoverTexts.push(`Ward Name: ${wardName} (${wardNum})<br>No Forecast data available for this ward`);
      }
    });

    setWardDataMap(newWardDataMap);
    setSeverityCounts(counts);

    const colorscale = [[0, '#2E7D32'], [0.5, '#F9A825'], [1, '#B71C1C']];
    const zmin = 1;
    const zmax = 3;

    const dataTrace = {
      type: 'choroplethmapbox',
      geojson: geojson,
      featureidkey: featureIdKey,
      locations: dataWardNumbers,
      z: dataValues,
      colorscale: colorscale,
      zmin: zmin,
      zmax: zmax,
      showscale: false,
      hoverinfo: 'text',
      hovertext: dataHoverTexts,
      marker: { 
        opacity: 0.7,
        line: { 
          color: '#000000',
          width: 1.5
        }
      },
      hoverlabel: { bgcolor: 'white', bordercolor: '#333', font: { size: 12 }, namelength: -1 }
    };

    const noDataTrace = {
      type: 'choroplethmapbox',
      geojson: geojson,
      featureidkey: featureIdKey,
      locations: noDataWardNumbers,
      z: Array(noDataWardNumbers.length).fill(0),
      colorscale: [[0, '#808080'], [1, '#404040']],
      zmin: 0,
      zmax: 1,
      showscale: false,
      hoverinfo: 'text',
      hovertext: noDataHoverTexts,
      marker: { 
        opacity: 0.7,
        line: { color: '#000000', width: 1.5 }
      },
      hoverlabel: { bgcolor: 'white', bordercolor: '#333', font: { size: 12 }, namelength: -1 }
    };

    let selectedWardTrace = null;
    if (selectedWard) {
      const selectedWardNum = selectedWard.id || selectedWard.properties?.[wardPropertyName];
      if (selectedWardNum) {
        selectedWardTrace = {
          name: 'selected-ward-highlight',
          type: 'choroplethmapbox',
          geojson: geojson,
          featureidkey: featureIdKey,
          locations: [selectedWardNum],
          z: [1],
          colorscale: [[0, 'rgba(255, 215, 0, 0.25)'], [1, 'rgba(255, 215, 0, 0.25)']],
          showscale: false,
          hoverinfo: 'skip',
          marker: { 
            opacity: 0.35,
            line: { color: '#FFD700', width: 5 }
          }
        };
      }
    }

    const traces = selectedWardTrace 
      ? [noDataTrace, dataTrace, selectedWardTrace]
      : [noDataTrace, dataTrace];

    addIndiaBoundaryTrace(traces);

    const selectedCenter = selectedWard?.geometry ? getGeometryBboxCenter(selectedWard.geometry) : null;
    const effectiveCenter = selectedCenter ? { lat: selectedCenter.lat, lon: selectedCenter.lon } : { lat: mapCenter.lat, lon: mapCenter.lon };
    const layout = {
      mapbox: {
        style: 'open-street-map',
        center: effectiveCenter,
        zoom: mapZoom
      },
      margin: { 
        t: 0, 
        b: 0, 
        l: 0, 
        r: window.innerWidth <= 480 ? 20 : (window.innerWidth <= 768 ? 40 : 50) 
      },
      showlegend: false,
      height: window.innerWidth <= 480 ? 300 : (window.innerWidth <= 768 ? 400 : 500),
      autosize: true,
      paper_bgcolor: 'rgba(0,0,0,0)',
      plot_bgcolor: 'rgba(0,0,0,0)',
      clickmode: 'event',
      uirevision: `forecast-construction-${forecastDate.getTime()}`,
      hovermode: 'closest',
      hoverdistance: 5,
      hoverlabel: { bgcolor: 'white', bordercolor: '#333', font: { size: 12 }, namelength: -1 }
    };

    setPlotData(traces);
    setPlotLayout(layout);
    setIsLoading(false);
    setError(null);
    
    console.log(`✅ Construction labor forecast map rendering completed successfully for day offset ${dayOffset}`);
  }, [indiaGeoJSON, selectedWard, setWardDataMap, getIndicatorThreshold, getGeometryBboxCenter, city, onForecastDaysAvailable, setForecastDaysAvailable]);

  // Render temperature map
  const renderTemperatureMap = useCallback(async (geojson, mapCenter, mapZoom) => {
    const temperatureData = await fetchTemperatureDataForAllWards(geojson, city);
    
    const dataLocations = [];
    const dataValues = [];
    const dataHoverTexts = [];
    const noDataLocations = [];
    const noDataHoverTexts = [];
    const newWardDataMap = {};

    geojson.features.forEach((feature, index) => {
      feature.id = index;
      const wardName = feature.properties.ward_name || feature.properties.Ward_Name || `Ward ${index + 1}`;
      const wardValue = temperatureData ? temperatureData[index] : null;
      const hasData = wardValue !== null && wardValue !== undefined && wardValue !== 0;

      newWardDataMap[index] = {
        id: index,
        name: wardName,
        value: wardValue,
        properties: feature.properties,
        geometry: feature.geometry
      };

      if (hasData) {
        dataLocations.push(index);
        dataValues.push(wardValue);
        dataHoverTexts.push(`${wardName}<br>Average Temperature: ${wardValue.toFixed(1)}°C`);
      } else {
        noDataLocations.push(index);
        noDataHoverTexts.push(`${wardName}<br>Average Temperature: N/A`);
      }
    });

    setWardDataMap(newWardDataMap);

    const minValue = dataValues.length > 0 ? Math.min(...dataValues) : 0;
    const maxValue = dataValues.length > 0 ? Math.max(...dataValues) : 0;

    const colorScale = [[0, '#1e40af'], [0.25, '#3b82f6'], [0.5, '#60a5fa'], [0.75, '#fbbf24'], [1, '#f97316']];

    const traces = [];

    if (dataLocations.length > 0) {
      traces.push({
        type: 'choroplethmapbox',
        geojson: geojson,
        locations: dataLocations,
        z: dataValues,
        colorscale: colorScale,
        zmin: minValue,
        zmax: maxValue,
        marker: { opacity: 0.8, line: { width: 3, color: '#000000', opacity: 1 } },
        hoverinfo: 'text',
        text: dataHoverTexts,
        colorbar: {
          title: 'Temperature (°C)',
          titlefont: { size: 12 },
          tickmode: 'array',
          tickvals: [minValue, maxValue],
          ticktext: [minValue.toFixed(1), maxValue.toFixed(1)]
        },
        showscale: true
      });
    }

    if (noDataLocations.length > 0) {
      traces.push({
        type: 'choroplethmapbox',
        geojson: geojson,
        locations: noDataLocations,
        z: new Array(noDataLocations.length).fill(0),
        colorscale: [[0, '#808080'], [1, '#404040']],
        zmin: 0,
        zmax: 1,
        marker: { opacity: 0.7, line: { width: 3, color: '#000000', opacity: 1 } },
        hoverinfo: 'text',
        text: noDataHoverTexts,
        showscale: false
      });
    }

    // Add selected ward highlight
    if (selectedWard && selectedWard.id !== undefined) {
      traces.push({
        name: 'selected-ward-highlight',
        type: 'choroplethmapbox',
        geojson: geojson,
        locations: [selectedWard.id],
        z: [1],
        hoverinfo: 'skip',
        colorscale: [[0, 'rgba(255, 215, 0, 0.25)'], [1, 'rgba(255, 215, 0, 0.25)']],
        zmin: 0,
        zmax: 1,
        marker: { opacity: 0.35, line: { width: 5, color: '#FFD700' } },
        showscale: false
      });
    }

    addIndiaBoundaryTrace(traces);
    setPlotData(traces);
    setPlotLayout({
      mapbox: {
        style: 'open-street-map',
        center: mapCenter,
        zoom: FIXED_ZOOM_LEVEL,
        bearing: 0,
        pitch: 0
      },
      margin: { 
        t: 0, 
        b: 0, 
        l: 0, 
        r: window.innerWidth <= 480 ? 20 : (window.innerWidth <= 768 ? 40 : 50) 
      },
      height: window.innerWidth <= 480 ? 300 : (window.innerWidth <= 768 ? 400 : 500),
      autosize: true,
      dragmode: 'zoom',
      clickmode: 'event',
      uirevision: 'constant',
      hovermode: 'closest',
      hoverdistance: 5,
      hoverlabel: { bgcolor: 'white', bordercolor: '#333', font: { size: 12 }, namelength: -1 }
    });
  }, [setWardDataMap, selectedWard, indiaGeoJSON]);

  // Listen for threshold changes
  useEffect(() => {
    const handleThresholdChange = async () => {
      const center = cityMapCenter || (currentGeoJSON ? calculateCityBounds(currentGeoJSON).center : null);
      if (!center || !currentGeoJSON) return;
      if (isGroundwater) {
        console.log('Threshold changed, re-rendering groundwater map...');
        await renderGroundwaterMap(currentGeoJSON, center, FIXED_ZOOM_LEVEL, groundwaterMode);
      } else if (isPowerOutage) {
        console.log('Threshold changed, re-rendering map...');
        await renderPowerOutageMap(currentGeoJSON, center, FIXED_ZOOM_LEVEL, powerOutageMode);
      } else if (isTransportDisruptions) {
        console.log('Threshold changed, re-rendering transport disruptions map...');
        await renderTransportDisruptionsMap(currentGeoJSON, center, FIXED_ZOOM_LEVEL, transportDisruptionsMode);
      } else if (isRoadClosures) {
        await renderSeverityParamMap(currentGeoJSON, center, FIXED_ZOOM_LEVEL, roadClosuresMode, { fetchFn: fetchRoadClosuresDataForWards, indicatorName: INDICATOR_NAMES.roadClosures, noDataLabel: 'No data' });
      } else if (isTrafficSignalFailures) {
        await renderSeverityParamMap(currentGeoJSON, center, FIXED_ZOOM_LEVEL, trafficSignalFailuresMode, { fetchFn: fetchTrafficSignalFailuresDataForWards, indicatorName: INDICATOR_NAMES.trafficSignal, noDataLabel: 'No data' });
      } else if (isCoolingSystemFailures) {
        await renderSeverityParamMap(currentGeoJSON, center, FIXED_ZOOM_LEVEL, coolingSystemFailuresMode, { fetchFn: fetchCoolingSystemFailuresDataForWards, indicatorName: INDICATOR_NAMES.coolingSystem, noDataLabel: 'No data', paramModes: [{ id: 'total_people_affected', field: 'total_people_affected', colorbarTitle: 'Total People Affected' }, { id: 'total_cooling_capacity_affected', field: 'total_cooling_capacity_affected', colorbarTitle: 'Cooling Capacity Affected (min)' }] });
      } else if (isSchoolClosures) {
        await renderSeverityParamMap(currentGeoJSON, center, FIXED_ZOOM_LEVEL, schoolClosuresMode, { fetchFn: fetchSchoolClosuresDataForWards, indicatorName: INDICATOR_NAMES.schoolClosures, noDataLabel: 'No data', paramModes: [{ id: 'school_closures_severity_ward_wise', field: 'school_closures_severity_ward_wise', colorbarTitle: 'School Closures Severity Ward Wise' }, { id: 'total_schools_in_ward', field: 'total_schools_in_ward', colorbarTitle: 'Total Schools in Ward' }] });
      } else if (isSchoolCooling) {
        await renderSeverityParamMap(currentGeoJSON, center, FIXED_ZOOM_LEVEL, schoolCoolingMode, { fetchFn: fetchSchoolCoolingDataForWards, indicatorName: INDICATOR_NAMES.schoolCooling, noDataLabel: 'No data', paramModes: [{ id: 'classrooms_with_cooling_infra', field: 'classrooms_with_cooling_infra', colorbarTitle: 'Classrooms with Cooling Infra' }, { id: 'total_classrooms_in_ward', field: 'total_classrooms_in_ward', colorbarTitle: 'Total Classrooms in Ward' }] });
      } else if (isWaterSupply) {
        await renderSeverityParamMap(currentGeoJSON, center, FIXED_ZOOM_LEVEL, waterSupplyMode, { fetchFn: fetchWaterSupplyDataForWards, indicatorName: INDICATOR_NAMES.waterSupply, noDataLabel: 'No data', paramModes: [{ id: 'daily_demand_lpcd', field: 'daily_demand_lpcd', colorbarTitle: 'Daily Demand (lpcd)' }, { id: 'daily_supply_lpcd', field: 'daily_supply_lpcd', colorbarTitle: 'Daily Supply (lpcd)' }] });
      } else if (isWaterTankers) {
        await renderSeverityParamMap(currentGeoJSON, center, FIXED_ZOOM_LEVEL, waterTankersMode, { fetchFn: fetchWaterTankersDataForWards, indicatorName: INDICATOR_NAMES.waterTankers, noDataLabel: 'No data', paramModes: [{ id: 'tanker_trips', field: 'tanker_trips', colorbarTitle: 'Tanker Trips' }, { id: 'emergency_requests', field: 'emergency_requests', colorbarTitle: 'Emergency Requests' }] });
      } else if (isHeatDifferential) {
        await renderSeverityParamMap(currentGeoJSON, center, FIXED_ZOOM_LEVEL, heatDifferentialMode, { fetchFn: fetchHeatDifferentialDataForWards, indicatorName: INDICATOR_NAMES.heatDifferential, noDataLabel: 'No data', paramModes: [{ id: 'avg_urban_temp_c', field: 'avg_urban_temp_c', colorbarTitle: 'Avg Urban Temp (°C)' }, { id: 'avg_rural_temp_c', field: 'avg_rural_temp_c', colorbarTitle: 'Avg Rural Temp (°C)' }] });
      } else if (isSurfaceTemperature) {
        await renderSeverityParamMap(currentGeoJSON, center, FIXED_ZOOM_LEVEL, surfaceTemperatureMode, { fetchFn: fetchSurfaceTemperatureDataForWards, indicatorName: INDICATOR_NAMES.surfaceTemperature, noDataLabel: 'No data', paramModes: [{ id: 'surface_temp_c', field: 'surface_temp_c', colorbarTitle: 'Surface Temp (°C)' }, { id: 'land_use_type', field: 'land_use_type', colorbarTitle: 'Land Use Type' }] });
      } else if (isShelterOccupancy) {
        await renderSeverityParamMap(currentGeoJSON, center, FIXED_ZOOM_LEVEL, shelterOccupancyMode, { fetchFn: fetchShelterOccupancyDataForWards, indicatorName: INDICATOR_NAMES.shelterOccupancy, noDataLabel: 'No data', paramModes: [{ id: 'occupancy_rate', field: 'occupancy_rate', colorbarTitle: 'Occupancy Rate (%)' }] });
      }
    };

    window.addEventListener('thresholdChanged', handleThresholdChange);
    return () => {
      window.removeEventListener('thresholdChanged', handleThresholdChange);
    };
  }, [isPowerOutage, isGroundwater, isTransportDisruptions, isRoadClosures, isTrafficSignalFailures, isCoolingSystemFailures, isSchoolClosures, isSchoolCooling, isWaterSupply, isWaterTankers, isHeatDifferential, isSurfaceTemperature, isShelterOccupancy, currentGeoJSON, cityMapCenter, powerOutageMode, groundwaterMode, transportDisruptionsMode, roadClosuresMode, trafficSignalFailuresMode, coolingSystemFailuresMode, schoolClosuresMode, schoolCoolingMode, waterSupplyMode, waterTankersMode, heatDifferentialMode, surfaceTemperatureMode, shelterOccupancyMode, renderPowerOutageMap, renderGroundwaterMap, renderTransportDisruptionsMap, renderSeverityParamMap]);

  // Load GeoJSON and render map
  useEffect(() => {
    const loadAndRenderMap = async () => {
      if (!city || !indicatorName) return;

      setIsLoading(true);
      setError(null);

      try {
        if (!hasIndicatorData(city, indicatorName)) {
          setIsLoading(false);
          setError('Data not available for this indicator');
          return;
        }

        let geojson = currentGeoJSON;
        if (!geojson) {
          geojson = await fetchGeoJSON(city);
          setCurrentGeoJSON(geojson);
        }

        // Calculate optimal center (zoom is fixed for consistent view)
        const cityBounds = calculateCityBounds(geojson);
        const mapCenter = cityBounds.center;
        
        setCityMapCenter(mapCenter);
        setCityMapZoom(FIXED_ZOOM_LEVEL);

        if (isGroundwater) {
          // Check if we're in forecast mode
          if (groundwaterMode === 'forecast') {
            try {
              const dateToForecast = forecastDay || new Date();
              await renderGroundwaterForecastMap(geojson, mapCenter, FIXED_ZOOM_LEVEL, dateToForecast);
            } catch (mapError) {
              console.error('Error rendering groundwater forecast map:', mapError);
              console.log('Falling back to base city map due to API error');
              await renderCityMap(geojson, mapCenter, FIXED_ZOOM_LEVEL);
              setError(`API Error: ${mapError.message}. Showing base map without data overlay.`);
            }
          } else {
            try {
              await renderGroundwaterMap(geojson, mapCenter, FIXED_ZOOM_LEVEL, groundwaterMode);
            } catch (mapError) {
              console.error('Error rendering groundwater map:', mapError);
              await renderCityMap(geojson, mapCenter, FIXED_ZOOM_LEVEL);
              setError(`API Error: ${mapError.message}. Showing base map without data overlay.`);
            }
          }
        } else if (isInformalWorkers) {
          // Check if we're in forecast mode
          if (informalWorkersMode === 'forecast') {
            try {
              const dateToForecast = forecastDay || new Date();
              const indicatorType =
                indicatorName === 'No show rate among informal workers'
                  ? 'no_show_rate'
                  : 'daily_wage_loss';
              await renderInformalWorkersForecastMap(geojson, mapCenter, FIXED_ZOOM_LEVEL, dateToForecast, indicatorType);
            } catch (mapError) {
              console.error('Error rendering informal workers forecast map:', mapError);
              await renderCityMap(geojson, mapCenter, FIXED_ZOOM_LEVEL);
              setError(`API Error: ${mapError.message}. Showing base map without data overlay.`);
            }
          } else {
            // Determine indicator type based on indicator name
            const indicatorType =
              indicatorName === 'No show rate among informal workers'
                ? 'no_show_rate'
                : 'daily_wage_loss';
            try {
              await renderInformalWorkersMap(geojson, mapCenter, FIXED_ZOOM_LEVEL, informalWorkersMode, indicatorType);
            } catch (mapError) {
              console.error('Error rendering informal workers map:', mapError);
              await renderCityMap(geojson, mapCenter, FIXED_ZOOM_LEVEL);
              setError(`API Error: ${mapError.message}. Showing base map without data overlay.`);
            }
          }
        } else if (isConstructionLabor) {
          // Check if we're in forecast mode
          if (constructionLaborMode === 'forecast') {
            try {
              const dateToForecast = forecastDay || new Date();
              const indicatorType =
                indicatorName === 'Lost labor hours due to heat stress'
                  ? 'lost_hours'
                  : indicatorName === '% of workers without access to shade/water'
                  ? 'no_shade_water'
                  : 'heat_illness';
              await renderConstructionLaborForecastMap(geojson, mapCenter, FIXED_ZOOM_LEVEL, dateToForecast, indicatorType);
            } catch (mapError) {
              console.error('Error rendering construction labor forecast map:', mapError);
              await renderCityMap(geojson, mapCenter, FIXED_ZOOM_LEVEL);
              setError(`API Error: ${mapError.message}. Showing base map without data overlay.`);
            }
          } else {
            // Determine indicator type based on indicator name
            const indicatorType =
              indicatorName === 'Lost labor hours due to heat stress'
                ? 'lost_hours'
                : indicatorName === '% of workers without access to shade/water'
                ? 'no_shade_water'
                : 'heat_illness';
            try {
              await renderConstructionLaborMap(geojson, mapCenter, FIXED_ZOOM_LEVEL, constructionLaborMode, indicatorType);
            } catch (mapError) {
              console.error('Error rendering construction labor map:', mapError);
              await renderCityMap(geojson, mapCenter, FIXED_ZOOM_LEVEL);
              setError(`API Error: ${mapError.message}. Showing base map without data overlay.`);
            }
          }
        } else if (isPowerOutage) {
          // Check if we're in forecast mode
          if (powerOutageMode === 'forecast') {
            try {
              const dateToForecast = forecastDay || new Date();
              await renderForecastMap(geojson, mapCenter, FIXED_ZOOM_LEVEL, dateToForecast);
            } catch (mapError) {
              console.error('Error rendering forecast map:', mapError);
              console.log('Falling back to base city map due to API error');
              await renderCityMap(geojson, mapCenter, FIXED_ZOOM_LEVEL);
              setError(`API Error: ${mapError.message}. Showing base map without data overlay.`);
            }
          } else {
            try {
              await renderPowerOutageMap(geojson, mapCenter, FIXED_ZOOM_LEVEL, powerOutageMode);
            } catch (mapError) {
              console.error('Error rendering power outage map:', mapError);
              await renderCityMap(geojson, mapCenter, FIXED_ZOOM_LEVEL);
              setError(`API Error: ${mapError.message}. Showing base map without data overlay.`);
            }
          }
        } else if (isTransportDisruptions) {
          try {
            await renderTransportDisruptionsMap(geojson, mapCenter, FIXED_ZOOM_LEVEL, transportDisruptionsMode);
          } catch (mapError) {
            console.error('Error rendering transport disruptions map:', mapError);
            await renderCityMap(geojson, mapCenter, FIXED_ZOOM_LEVEL);
            setError(`API Error: ${mapError.message}. Showing base map without data overlay.`);
          }
        } else if (isRoadClosures) {
          try {
            await renderSeverityParamMap(geojson, mapCenter, FIXED_ZOOM_LEVEL, roadClosuresMode, { fetchFn: fetchRoadClosuresDataForWards, indicatorName: INDICATOR_NAMES.roadClosures, noDataLabel: 'No data' });
          } catch (e) { await renderCityMap(geojson, mapCenter, FIXED_ZOOM_LEVEL); setError(`API Error: ${e.message}`); }
        } else if (isTrafficSignalFailures) {
          try {
            await renderSeverityParamMap(geojson, mapCenter, FIXED_ZOOM_LEVEL, trafficSignalFailuresMode, { fetchFn: fetchTrafficSignalFailuresDataForWards, indicatorName: INDICATOR_NAMES.trafficSignal, noDataLabel: 'No data' });
          } catch (e) { await renderCityMap(geojson, mapCenter, FIXED_ZOOM_LEVEL); setError(`API Error: ${e.message}`); }
        } else if (isCoolingSystemFailures) {
          try {
            await renderSeverityParamMap(geojson, mapCenter, FIXED_ZOOM_LEVEL, coolingSystemFailuresMode, {
              fetchFn: fetchCoolingSystemFailuresDataForWards, indicatorName: INDICATOR_NAMES.coolingSystem, noDataLabel: 'No data',
              paramModes: [{ id: 'total_people_affected', field: 'total_people_affected', colorbarTitle: 'Total People Affected' }, { id: 'total_cooling_capacity_affected', field: 'total_cooling_capacity_affected', colorbarTitle: 'Cooling Capacity Affected (min)' }]
            });
          } catch (e) { await renderCityMap(geojson, mapCenter, FIXED_ZOOM_LEVEL); setError(`API Error: ${e.message}`); }
        } else if (isSchoolClosures) {
          try {
            await renderSeverityParamMap(geojson, mapCenter, FIXED_ZOOM_LEVEL, schoolClosuresMode, {
              fetchFn: fetchSchoolClosuresDataForWards, indicatorName: INDICATOR_NAMES.schoolClosures, noDataLabel: 'No data',
              paramModes: [{ id: 'school_closures_severity_ward_wise', field: 'school_closures_severity_ward_wise', colorbarTitle: 'School Closures Severity Ward Wise' }, { id: 'total_schools_in_ward', field: 'total_schools_in_ward', colorbarTitle: 'Total Schools in Ward' }]
            });
          } catch (e) { await renderCityMap(geojson, mapCenter, FIXED_ZOOM_LEVEL); setError(`API Error: ${e.message}`); }
        } else if (isSchoolCooling) {
          try {
            await renderSeverityParamMap(geojson, mapCenter, FIXED_ZOOM_LEVEL, schoolCoolingMode, {
              fetchFn: fetchSchoolCoolingDataForWards, indicatorName: INDICATOR_NAMES.schoolCooling, noDataLabel: 'No data',
              paramModes: [{ id: 'classrooms_with_cooling_infra', field: 'classrooms_with_cooling_infra', colorbarTitle: 'Classrooms with Cooling Infra' }, { id: 'total_classrooms_in_ward', field: 'total_classrooms_in_ward', colorbarTitle: 'Total Classrooms in Ward' }]
            });
          } catch (e) { await renderCityMap(geojson, mapCenter, FIXED_ZOOM_LEVEL); setError(`API Error: ${e.message}`); }
        } else if (isWaterSupply) {
          try {
            await renderSeverityParamMap(geojson, mapCenter, FIXED_ZOOM_LEVEL, waterSupplyMode, {
              fetchFn: fetchWaterSupplyDataForWards, indicatorName: INDICATOR_NAMES.waterSupply, noDataLabel: 'No data',
              paramModes: [{ id: 'daily_demand_lpcd', field: 'daily_demand_lpcd', colorbarTitle: 'Daily Demand (lpcd)' }, { id: 'daily_supply_lpcd', field: 'daily_supply_lpcd', colorbarTitle: 'Daily Supply (lpcd)' }]
            });
          } catch (e) { await renderCityMap(geojson, mapCenter, FIXED_ZOOM_LEVEL); setError(`API Error: ${e.message}`); }
        } else if (isWaterTankers) {
          try {
            await renderSeverityParamMap(geojson, mapCenter, FIXED_ZOOM_LEVEL, waterTankersMode, {
              fetchFn: fetchWaterTankersDataForWards, indicatorName: INDICATOR_NAMES.waterTankers, noDataLabel: 'No data',
              paramModes: [{ id: 'tanker_trips', field: 'tanker_trips', colorbarTitle: 'Tanker Trips' }, { id: 'emergency_requests', field: 'emergency_requests', colorbarTitle: 'Emergency Requests' }]
            });
          } catch (e) { await renderCityMap(geojson, mapCenter, FIXED_ZOOM_LEVEL); setError(`API Error: ${e.message}`); }
        } else if (isHeatDifferential) {
          try {
            await renderSeverityParamMap(geojson, mapCenter, FIXED_ZOOM_LEVEL, heatDifferentialMode, {
              fetchFn: fetchHeatDifferentialDataForWards, indicatorName: INDICATOR_NAMES.heatDifferential, noDataLabel: 'No data',
              paramModes: [{ id: 'avg_urban_temp_c', field: 'avg_urban_temp_c', colorbarTitle: 'Avg Urban Temp (°C)' }, { id: 'avg_rural_temp_c', field: 'avg_rural_temp_c', colorbarTitle: 'Avg Rural Temp (°C)' }]
            });
          } catch (e) { await renderCityMap(geojson, mapCenter, FIXED_ZOOM_LEVEL); setError(`API Error: ${e.message}`); }
        } else if (isSurfaceTemperature) {
          try {
            await renderSeverityParamMap(geojson, mapCenter, FIXED_ZOOM_LEVEL, surfaceTemperatureMode, {
              fetchFn: fetchSurfaceTemperatureDataForWards, indicatorName: INDICATOR_NAMES.surfaceTemperature, noDataLabel: 'No data',
              paramModes: [{ id: 'surface_temp_c', field: 'surface_temp_c', colorbarTitle: 'Surface Temp (°C)' }, { id: 'land_use_type', field: 'land_use_type', colorbarTitle: 'Land Use Type' }]
            });
          } catch (e) { await renderCityMap(geojson, mapCenter, FIXED_ZOOM_LEVEL); setError(`API Error: ${e.message}`); }
        } else if (isShelterOccupancy) {
          try {
            await renderSeverityParamMap(geojson, mapCenter, FIXED_ZOOM_LEVEL, shelterOccupancyMode, {
              fetchFn: fetchShelterOccupancyDataForWards, indicatorName: INDICATOR_NAMES.shelterOccupancy, noDataLabel: 'No data',
              paramModes: [{ id: 'occupancy_rate', field: 'occupancy_rate', colorbarTitle: 'Occupancy Rate (%)' }]
            });
          } catch (e) { await renderCityMap(geojson, mapCenter, FIXED_ZOOM_LEVEL); setError(`API Error: ${e.message}`); }
        } else {
          await renderCityMap(geojson, mapCenter, FIXED_ZOOM_LEVEL);
        }
      } catch (err) {
        console.error('Error loading map:', err);
        // Try to show base map even on error
        try {
          if (currentGeoJSON) {
            const cityBounds = calculateCityBounds(currentGeoJSON);
            const mapCenter = cityBounds.center;
            await renderCityMap(currentGeoJSON, mapCenter, FIXED_ZOOM_LEVEL);
            setError(`Map loaded with limited functionality: ${err.message}`);
          } else {
            const errorMessage = err.message || 'Unknown error occurred while loading map';
            setError(errorMessage);
          }
        } catch (fallbackError) {
          const errorMessage = err.message || 'Unknown error occurred while loading map';
          setError(errorMessage);
        }
      } finally {
        setIsLoading(false);
      }
    };

    loadAndRenderMap();
  // Intentionally omit currentThreshold/groundwaterThreshold from deps: they are read inside
  // renderPowerOutageMap/renderGroundwaterMap via getIndicatorThreshold() at call time.
  // Including them caused blinking: threshold is often an object (new ref each render), so the
  // effect re-ran every render and repeatedly reloaded the map. Threshold changes are handled
  // by the thresholdChanged event listener which explicitly re-renders the map.
  }, [city, indicatorName, isPowerOutage, powerOutageMode, isGroundwater, groundwaterMode, isInformalWorkers, informalWorkersMode, isConstructionLabor, constructionLaborMode, isTransportDisruptions, transportDisruptionsMode, isRoadClosures, roadClosuresMode, isTrafficSignalFailures, trafficSignalFailuresMode, isCoolingSystemFailures, coolingSystemFailuresMode, isSchoolClosures, schoolClosuresMode, isSchoolCooling, schoolCoolingMode, isWaterSupply, waterSupplyMode, isWaterTankers, waterTankersMode, isHeatDifferential, heatDifferentialMode, isSurfaceTemperature, surfaceTemperatureMode, isShelterOccupancy, shelterOccupancyMode, forecastDay, selectedDate, startDate, endDate, currentGeoJSON, setCurrentGeoJSON, setCityMapCenter, setCityMapZoom, hasIndicatorData, renderCityMap, renderPowerOutageMap, renderGroundwaterMap, renderGroundwaterForecastMap, renderForecastMap, renderTemperatureMap, renderInformalWorkersMap, renderConstructionLaborMap, renderInformalWorkersForecastMap, renderConstructionLaborForecastMap, renderTransportDisruptionsMap, renderSeverityParamMap, getIndicatorThreshold]);

  // Render the map using Plotly.newPlot when data changes
  useEffect(() => {
    if (!mapDivRef.current || plotData.length === 0 || isLoading) return;

    const config = {
      responsive: true,
      displayModeBar: true,
      displaylogo: false,
      modeBarButtonsToRemove: ['pan2d', 'lasso2d', 'select2d'],
      modeBarButtonsToAdd: [],
      scrollZoom: true
    };

    // Use Plotly.newPlot to render the map directly and ensure it resets view
    const getResponsiveHeight = () => {
      if (window.innerWidth <= 480) return 300;
      if (window.innerWidth <= 768) return 400;
      return 500;
    };
    
    // Use getBoundingClientRect for more accurate measurements
    const containerRect = mapDivRef.current.getBoundingClientRect();
    const containerHeight = containerRect.height || mapDivRef.current.offsetHeight || getResponsiveHeight();
    const containerWidth = containerRect.width || mapDivRef.current.offsetWidth || mapDivRef.current.clientWidth;
    
    // Ensure width is set in plotLayout and override any existing width
    const finalLayout = {
      ...plotLayout,
      height: containerHeight,
      width: containerWidth,
      autosize: true,
      margin: { l: 0, r: 0, t: 0, b: 0, pad: 0 }
    };
    
    let cancelled = false;
    let timeout1;
    let timeout2;

    const safeRelayout = (node, layout) => {
      if (cancelled || !node) return Promise.resolve();
      try {
        return Plotly.relayout(node, layout);
      } catch (e) {
        return Promise.resolve();
      }
    };

    Plotly.newPlot(mapDivRef.current, plotData, finalLayout, config).then(() => {
      if (cancelled || !mapDivRef.current) return;
      // Resize plot to ensure it fills the container
      try {
        Plotly.Plots.resize(mapDivRef.current);
      } catch (e) { /* no-op */ }

      // Force resize multiple times to ensure proper rendering
      timeout1 = setTimeout(() => {
        if (cancelled || !mapDivRef.current) return;
        const rect = mapDivRef.current.getBoundingClientRect();
        safeRelayout(mapDivRef.current, { width: rect.width, height: rect.height }).then(() => {
          if (cancelled || !mapDivRef.current) return;
          try {
            Plotly.Plots.resize(mapDivRef.current);
          } catch (e) { /* no-op */ }
        });

        timeout2 = setTimeout(() => {
          if (cancelled || !mapDivRef.current) return;
          const rect2 = mapDivRef.current.getBoundingClientRect();
          safeRelayout(mapDivRef.current, { width: rect2.width, height: rect2.height }).then(() => {
            if (cancelled || !mapDivRef.current) return;
            try {
              Plotly.Plots.resize(mapDivRef.current);
            } catch (e) { /* no-op */ }
          });
        }, 100);
      }, 100);
      // Add click event handler for ward selection (disabled for severity mode)
      const allowWardClick = (isPowerOutage && powerOutageMode !== 'severity') || (isGroundwater && groundwaterMode !== 'severity') || (isInformalWorkers && informalWorkersMode !== 'severity') || (isConstructionLabor && constructionLaborMode !== 'severity') || (isTransportDisruptions && transportDisruptionsMode !== 'severity') || (isRoadClosures && roadClosuresMode !== 'severity') || (isTrafficSignalFailures && trafficSignalFailuresMode !== 'severity') || (isCoolingSystemFailures && coolingSystemFailuresMode !== 'severity') || (isSchoolClosures && schoolClosuresMode !== 'severity') || (isSchoolCooling && schoolCoolingMode !== 'severity') || (isWaterSupply && waterSupplyMode !== 'severity') || (isWaterTankers && waterTankersMode !== 'severity') || (isHeatDifferential && heatDifferentialMode !== 'severity') || (isSurfaceTemperature && surfaceTemperatureMode !== 'severity') || (isShelterOccupancy && shelterOccupancyMode !== 'severity');
      if (mapDivRef.current && allowWardClick) {
        mapDivRef.current.on('plotly_click', (data) => {
          if (data.points && data.points.length > 0) {
            const point = data.points[0];
            const wardId = point.location;
            const wardInfo = wardDataMap[wardId];
            if (wardInfo) {
              setSelectedWard(wardInfo);
              console.log('Selected ward:', wardInfo);
            }
          }
        });
      }
      
      // Add mouse leave event handler to hide hover tooltip when mouse leaves map
      if (mapDivRef.current) {
        const handleMouseLeave = (e) => {
          // Only hide if mouse is actually leaving the map container
          // Check if the related target is outside the map container
          if (!mapDivRef.current.contains(e.relatedTarget)) {
            // Remove tooltip elements but keep hoverlayer intact
            // Plotly will recreate tooltips when hovering over data points again
            const hoverTexts = mapDivRef.current.querySelectorAll('.hovertext');
            hoverTexts.forEach(el => {
              el.remove();
            });
          }
        };
        
        mapDivRef.current.addEventListener('mouseleave', handleMouseLeave);
        
        // Store handler for cleanup
        mapDivRef.current._hoverCleanup = handleMouseLeave;
      }
    });

    console.log('Map plotted with Plotly.newPlot');

    // Add resize handler to ensure map fills container
    let resizeTimeout;
    const handleResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        if (cancelled || !mapDivRef.current) return;
        const containerRect = mapDivRef.current.getBoundingClientRect();
        const newHeight = containerRect.height || (window.innerWidth <= 480 ? 300 : (window.innerWidth <= 768 ? 400 : 500));
        const newWidth = containerRect.width || mapDivRef.current.offsetWidth;
        const severityModeNoMargin = (isPowerOutage && powerOutageMode === 'severity') || (isGroundwater && groundwaterMode === 'severity') || (isInformalWorkers && informalWorkersMode === 'severity') || (isConstructionLabor && constructionLaborMode === 'severity') || (isTransportDisruptions && transportDisruptionsMode === 'severity') || (isRoadClosures && roadClosuresMode === 'severity') || (isTrafficSignalFailures && trafficSignalFailuresMode === 'severity') || (isCoolingSystemFailures && coolingSystemFailuresMode === 'severity') || (isSchoolClosures && schoolClosuresMode === 'severity') || (isSchoolCooling && schoolCoolingMode === 'severity') || (isWaterSupply && waterSupplyMode === 'severity') || (isWaterTankers && waterTankersMode === 'severity') || (isHeatDifferential && heatDifferentialMode === 'severity') || (isSurfaceTemperature && surfaceTemperatureMode === 'severity') || (isShelterOccupancy && shelterOccupancyMode === 'severity');
        const newMarginR = severityModeNoMargin ? 0 : (window.innerWidth <= 480 ? 20 : (window.innerWidth <= 768 ? 40 : 50));
        safeRelayout(mapDivRef.current, {
          height: newHeight,
          width: newWidth,
          margin: { l: 0, r: newMarginR, t: 0, b: 0, pad: 0 }
        }).then(() => {
          if (cancelled || !mapDivRef.current) return;
          try {
            Plotly.Plots.resize(mapDivRef.current);
          } catch (e) { /* no-op */ }
        });
      }, 150);
    };
    window.addEventListener('resize', handleResize);

    // Cleanup
    return () => {
      cancelled = true;
      clearTimeout(resizeTimeout);
      clearTimeout(timeout1);
      clearTimeout(timeout2);
      window.removeEventListener('resize', handleResize);
      if (mapDivRef.current) {
        if (mapDivRef.current._hoverCleanup) {
          mapDivRef.current.removeEventListener('mouseleave', mapDivRef.current._hoverCleanup);
          delete mapDivRef.current._hoverCleanup;
        }
        try {
          Plotly.purge(mapDivRef.current);
        } catch (e) { /* no-op */ }
      }
    };
  }, [plotData, plotLayout, isLoading, wardDataMap, setSelectedWard, powerOutageMode, isPowerOutage, isGroundwater, groundwaterMode, isInformalWorkers, informalWorkersMode, isConstructionLabor, constructionLaborMode, isTransportDisruptions, transportDisruptionsMode, isRoadClosures, roadClosuresMode, isTrafficSignalFailures, trafficSignalFailuresMode, isCoolingSystemFailures, coolingSystemFailuresMode, isSchoolClosures, schoolClosuresMode, isSchoolCooling, schoolCoolingMode, isWaterSupply, waterSupplyMode, isWaterTankers, waterTankersMode, isHeatDifferential, heatDifferentialMode, isSurfaceTemperature, surfaceTemperatureMode, isShelterOccupancy, shelterOccupancyMode]);

  // Note: We intentionally do NOT call Plotly.relayout to recenter mapbox on selection.
  // Some Plotly/mapbox-gl builds can throw "style.version" errors when relayout runs mid-render.
  // Instead, centering is handled by regenerating plot layouts with a selected-ward center.

  // Format month and year from selectedDate
  const formatMonthYear = (date) => {
    if (!date) return '';
    const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'];
    const month = monthNames[date.getMonth()];
    const year = date.getFullYear();
    return `${month} ${year}`;
  };

  // Get map title based on mode
  const getMapTitle = () => {
    const dateForTitle = selectedDate || startDate || new Date();
    const monthYear = formatMonthYear(dateForTitle);

    if (isGroundwater) {
      if (groundwaterMode === 'forecast') {
        const forecastDate = forecastDay || new Date();
        const forecastDateStr = forecastDate.toLocaleDateString('en-US', { 
          weekday: 'short', 
          year: 'numeric', 
          month: 'short', 
          day: 'numeric' 
        });
        return `Forecasted Groundwater Severity - ${forecastDateStr}`;
      }
      if (groundwaterMode === 'severity') return `Groundwater Severity - ${monthYear}`;
      if (groundwaterMode === 'gwl') return `Current Groundwater Level (m) - ${monthYear}`;
      if (groundwaterMode === 'depletion_pct') return `Groundwater Depletion % - ${monthYear}`;
      return null;
    }
    if (isTransportDisruptions) {
      if (transportDisruptionsMode === 'severity') return `Public Transport Disruption Severity - ${monthYear}`;
      if (transportDisruptionsMode === 'commuters_affected') return `Commuters Affected - ${monthYear}`;
      if (transportDisruptionsMode === 'disruption_minutes') return `Disruption Minutes - ${monthYear}`;
      return null;
    }
    if (isRoadClosures) return `Road/Bridge Degradation or Closures - ${monthYear}`;
    if (isTrafficSignalFailures) return `Traffic Signal / Equipment Failures - ${monthYear}`;
    if (isCoolingSystemFailures) {
      if (coolingSystemFailuresMode === 'severity') return `Cooling System Failure Severity - ${monthYear}`;
      if (coolingSystemFailuresMode === 'total_people_affected') return `Total People Affected - ${monthYear}`;
      if (coolingSystemFailuresMode === 'total_cooling_capacity_affected') return `Total Cooling Capacity Affected - ${monthYear}`;
      return null;
    }
    if (isSchoolClosures) {
      if (schoolClosuresMode === 'severity') return `School/College Closure Days Severity - ${monthYear}`;
      if (schoolClosuresMode === 'school_closures_severity_ward_wise') return `School Closures Severity Ward Wise - ${monthYear}`;
      if (schoolClosuresMode === 'total_schools_in_ward') return `Total Schools in Ward - ${monthYear}`;
      return null;
    }
    if (isSchoolCooling) {
      if (schoolCoolingMode === 'severity') return `Schools Without Cooling Severity - ${monthYear}`;
      if (schoolCoolingMode === 'classrooms_with_cooling_infra') return `Classrooms with Cooling Infra - ${monthYear}`;
      if (schoolCoolingMode === 'total_classrooms_in_ward') return `Total Classrooms in Ward - ${monthYear}`;
      return null;
    }
    if (isWaterSupply) {
      if (waterSupplyMode === 'severity') return `Drop in Water Availability Severity - ${monthYear}`;
      if (waterSupplyMode === 'daily_demand_lpcd') return `Daily Demand LPCD - ${monthYear}`;
      if (waterSupplyMode === 'daily_supply_lpcd') return `Daily Supply LPCD - ${monthYear}`;
      return null;
    }
    if (isWaterTankers) {
      if (waterTankersMode === 'severity') return `Wards Dependent on Water Tankers Severity - ${monthYear}`;
      if (waterTankersMode === 'tanker_trips') return `Tanker Trips - ${monthYear}`;
      if (waterTankersMode === 'emergency_requests') return `Emergency Requests - ${monthYear}`;
      return null;
    }
    if (isHeatDifferential) {
      if (heatDifferentialMode === 'severity') return `Temperature Differential Severity - ${monthYear}`;
      if (heatDifferentialMode === 'avg_urban_temp_c') return `Avg Urban Temp (°C) - ${monthYear}`;
      if (heatDifferentialMode === 'avg_rural_temp_c') return `Avg Rural Temp (°C) - ${monthYear}`;
      return null;
    }
    if (isSurfaceTemperature) {
      if (surfaceTemperatureMode === 'severity') return `Surface Temperature & Low Vegetation Severity - ${monthYear}`;
      if (surfaceTemperatureMode === 'surface_temp_c') return `Surface Temp (°C) - ${monthYear}`;
      if (surfaceTemperatureMode === 'land_use_type') return `Land Use Type - ${monthYear}`;
      return null;
    }
    if (isShelterOccupancy) {
      if (shelterOccupancyMode === 'severity') return `Homeless and Street Dwellers Severity - ${monthYear}`;
      if (shelterOccupancyMode === 'occupancy_rate') return `Occupancy Rate - ${monthYear}`;
      return null;
    }
    if (!isPowerOutage) return null;

    if (powerOutageMode === 'severity') {
      return `Severity Level of Wards - ${monthYear}`;
    } else if (powerOutageMode === 'consumers') {
      return `City-Level Consumers Affected - ${monthYear}`;
    } else if (powerOutageMode === 'duration') {
      return `City-Level Unplanned Power Outage Duration - ${monthYear}`;
    }
    return null;
  };

  const mapTitle = getMapTitle();

  if (error) {
    return (
      <div className="map-container" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', background: '#f8f9fa', borderRadius: '8px', position: 'relative' }}>
        {mapTitle && (
          <div style={{
            position: 'absolute',
            top: 0,
            left: '50%',
            transform: 'translateX(-50%)',
            padding: '0.5rem 1rem',
            background: 'rgba(255, 255, 255, 0.95)',
            borderRadius: '0 0 6px 6px',
            fontSize: '0.9rem',
            fontWeight: 600,
            color: '#2c3e50',
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
            zIndex: 10,
            whiteSpace: 'nowrap',
            willChange: 'transform',
            backfaceVisibility: 'hidden'
          }}>
            {mapTitle}
          </div>
        )}
        <div style={{ textAlign: 'center', padding: '2rem', color: '#666' }}>
          <div style={{ fontSize: '1rem', fontWeight: 500 }}>{error}</div>
        </div>
      </div>
    );
  }

  return (
    <div 
      ref={mapDivRef}
      className="map-container" 
      style={{ 
        minHeight: window.innerWidth <= 480 ? '300px' : (window.innerWidth <= 768 ? '400px' : '500px'), 
        height: '100%', 
        padding: 0, 
        margin: 0, 
        position: 'relative' 
      }}
    >
      {mapTitle && (
        <div style={{
          position: 'absolute',
          top: 0,
          left: '50%',
          transform: 'translateX(-50%)',
          padding: '0.5rem 1rem',
          background: 'rgba(255, 255, 255, 0.95)',
          borderRadius: '0 0 6px 6px',
          fontSize: window.innerWidth <= 480 ? '0.75rem' : (window.innerWidth <= 768 ? '0.85rem' : '0.9rem'),
          fontWeight: 600,
          color: '#2c3e50',
          boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
          zIndex: 10,
          whiteSpace: window.innerWidth <= 480 ? 'normal' : 'nowrap',
          maxWidth: window.innerWidth <= 480 ? '90%' : 'auto',
          textAlign: window.innerWidth <= 480 ? 'center' : 'left',
          pointerEvents: 'none',
          willChange: 'transform',
          backfaceVisibility: 'hidden'
        }}>
          {mapTitle}
        </div>
      )}
      {selectedWard && (
        <button
          type="button"
          onClick={() => setSelectedWard(null)}
          style={{
            position: 'absolute',
            top: 10,
            right: 10,
            zIndex: 1002,
            pointerEvents: 'auto',
            background: 'rgba(255, 255, 255, 0.95)',
            border: '1px solid #ced4da',
            borderRadius: 6,
            padding: '0.4rem 0.6rem',
            fontSize: '0.75rem',
            cursor: 'pointer',
            boxShadow: '0 2px 6px rgba(0,0,0,0.12)'
          }}
          title="Clear selected ward and reset view"
        >
          Reset view
        </button>
      )}
      {isLoading && (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#666', gap: '0.5rem', padding: '2rem', position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(255, 255, 255, 0.9)', zIndex: 100 }}>
          <div>Loading map...</div>
          {error && <div style={{ fontSize: '0.85rem', color: '#c0392b', marginTop: '0.5rem', textAlign: 'center' }}>{error}</div>}
        </div>
      )}
      {error && !isLoading && plotData.length > 0 && (
        <div style={{ 
          position: 'absolute', 
          top: '10px', 
          right: '10px', 
          background: '#fff3cd', 
          border: '1px solid #ffc107', 
          borderRadius: '4px', 
          padding: '0.5rem 0.75rem', 
          fontSize: '0.75rem', 
          color: '#856404',
          zIndex: 1001,
          maxWidth: '300px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
        }}>
          ⚠️ {error}
        </div>
      )}
      
      {/* Forecast Date Selector - Show when in forecast mode */}
      {((isPowerOutage && powerOutageMode === 'forecast') || (isGroundwater && groundwaterMode === 'forecast') || (isInformalWorkers && informalWorkersMode === 'forecast') || (isConstructionLabor && constructionLaborMode === 'forecast')) && forecastDay && onForecastDayChange && (
        <div 
          className="map-forecast-selector-overlay"
          style={{
            position: 'absolute',
            bottom: 0,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 1001,
            pointerEvents: 'auto',
            width: 'auto',
            minWidth: 'min(520px, calc(100% - 1rem))',
            maxWidth: 'calc(100% - 1rem)',
            borderRadius: '8px 8px 0 0',
            overflow: 'hidden',
            boxShadow: '0 -2px 8px rgba(0,0,0,0.15)',
            whiteSpace: windowSize.width <= 480 ? 'normal' : 'nowrap'
          }}
          onMouseEnter={(e) => {
            e.stopPropagation();
            const hoverTexts = mapDivRef.current?.querySelectorAll('.hovertext');
            if (hoverTexts) {
              hoverTexts.forEach(el => {
                el.remove();
              });
            }
            const hoverLayer = mapDivRef.current?.querySelector('.hoverlayer');
            if (hoverLayer) {
              hoverLayer.style.pointerEvents = 'none';
            }
          }}
          onMouseMove={(e) => {
            e.stopPropagation();
            const hoverLayer = mapDivRef.current?.querySelector('.hoverlayer');
            if (hoverLayer) {
              hoverLayer.style.pointerEvents = 'none';
            }
          }}
          onMouseLeave={(e) => {
            e.stopPropagation();
            const hoverLayer = mapDivRef.current?.querySelector('.hoverlayer');
            if (hoverLayer) {
              hoverLayer.style.pointerEvents = 'auto';
            }
          }}
        >
          <ForecastDateSelector
            selectedDate={forecastDay}
            onDateChange={onForecastDayChange}
            availableDaysCount={forecastDaysAvailable}
            severityCounts={severityCounts}
          />
        </div>
      )}
      
      {/* Date Range Selector Overlay - Show when Map View is toggled on */}
      {showDateSelector && (isPowerOutage || isTransportDisruptions || isRoadClosures || isTrafficSignalFailures || isCoolingSystemFailures || isSchoolClosures || isSchoolCooling || isWaterSupply || isWaterTankers || isHeatDifferential || isSurfaceTemperature || isShelterOccupancy) && availableMonths && (
        <div 
          className="map-date-selector-overlay"
          style={{
            position: 'absolute',
            bottom: 0,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 1001,
            pointerEvents: 'auto',
            width: 'auto',
            minWidth: 'fit-content',
            maxWidth: 'calc(100% - 1rem)',
            borderRadius: '8px 8px 0 0',
            overflow: 'hidden',
            boxShadow: '0 -2px 8px rgba(0,0,0,0.15)',
            whiteSpace: windowSize.width <= 480 ? 'normal' : 'nowrap'
          }}
          onMouseEnter={(e) => {
            // Prevent map hover when mouse enters date selector
            e.stopPropagation();
            // Hide any existing hover tooltips
            const hoverTexts = mapDivRef.current?.querySelectorAll('.hovertext');
            if (hoverTexts) {
              hoverTexts.forEach(el => {
                el.remove();
              });
            }
            // Also hide hoverlayer
            const hoverLayer = mapDivRef.current?.querySelector('.hoverlayer');
            if (hoverLayer) {
              hoverLayer.style.pointerEvents = 'none';
            }
          }}
          onMouseMove={(e) => {
            // Prevent map hover events when moving over date selector
            e.stopPropagation();
            // Keep hoverlayer disabled while over date selector
            const hoverLayer = mapDivRef.current?.querySelector('.hoverlayer');
            if (hoverLayer) {
              hoverLayer.style.pointerEvents = 'none';
            }
          }}
          onMouseLeave={(e) => {
            // Re-enable hoverlayer when mouse leaves date selector
            e.stopPropagation();
            const hoverLayer = mapDivRef.current?.querySelector('.hoverlayer');
            if (hoverLayer) {
              hoverLayer.style.pointerEvents = 'auto';
            }
          }}
        >
          <DateRangeSelector
            startDate={startDate}
            endDate={endDate}
            onStartDateChange={onStartDateChange}
            onEndDateChange={onEndDateChange}
            availableMonths={availableMonths}
          />
        </div>
      )}
    </div>
  );
};

export default MapContainer;
